import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, AlertCircle, CheckCircle2, Clock, UserCheck, PauseCircle,
  PlayCircle, XCircle, Image as ImageIcon, CalendarDays, Pencil,
  ArrowUp, ArrowDown, ArrowUpDown, X, ChevronDown, ChevronUp, FileText, Filter,
  ExternalLink, ShoppingCart, Wrench, FileClock,
} from 'lucide-react';
import { diasParado, aguardandoDe, trabalhoAcabou } from './chamados-fila-utils';
// ⚠️ O vocabulário dos status é COMPARTILHADO (fila, ficha do equipamento e
//    pop-up do calendário) — ver o cabeçalho do arquivo para o porquê.
import { ROTULO_STATUS } from '../chamados-rotulos';
// ⚠️ ESPELHO do `server/chamados/chamados-abas.js` (as abas são a esteira desde
//    01/09/2026). O `filtrarPelaAba` é a rede contra o backend velho da janela de
//    deploy — ver o cabeçalho de lá antes de achar que é redundância inútil.
import { ABAS, statusDaAba, filtrarPelaAba, contagemPorAba } from './chamados-abas';
import ChamadosPecas from './ChamadosPecas';
import useAutosaveRascunho from '../../../utils/useAutosaveRascunho';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
import { useCapacidades } from '../../../utils/capacidades';
// ⚠️ Componente COMPARTILHADO com o módulo Manutenção (Certificação e
//    Conservação). Ele exige que o módulo dono da foto exponha um proxy
//    `GET <apiBase>/foto?url=` — o dos Chamados nasceu em 21/08/2026 junto com
//    esta tela. Apontar para o proxy de outro submódulo dá 404, não foto trocada.
import ImagemSharePoint from '../../ImagemSharePoint';
import Paginacao, { POR_PAGINA } from '../../Paginacao';
import EscolherAnexo from '../../EscolherAnexo';
import { podarFiltros } from './chamados-filtros';
import useTelaMobile from '../../../utils/useTelaMobile';
// ⚠️ ESPELHO de `server/chamados/chamados-imagem.js` (travado por teste) — aqui
//    só o rótulo de leitura dos canais e o de destino.
import { DESTINOS_IMAGEM, ehSolicitacaoImagem, rotuloCanais } from './chamados-imagem-utils';
// ⚠️ ESPELHO de `server/chamados/atendimento/chamados-chip.js` (DEM-00027) — o
//    tipo, o rótulo do motivo e os rótulos próprios de status na aba de chip.
import { ehChip, ROTULO_MOTIVO as ROTULO_MOTIVO_CHIP, ROTULO_STATUS_CHIP } from './chamados-chip-utils';

// =====================================================================
// Chamados — a FILA DE TRABALHO (atos 2, 3 e 4).
//
// ⚠️ UMA TELA PARA TODOS OS PAPÉIS, não uma por papel. A lista é a mesma; o que
// muda são as AÇÕES de cada linha, e elas já dependem de quem olha. Duas telas
// mostrando a mesma tabela com filtro diferente seriam duas coisas para manter e
// uma dúvida a mais ("em qual delas está o meu chamado?"). É a mesma conclusão a
// que a Certificação e a Conservação chegaram em 17-18/08/2026, quando as duas
// árvores viraram lista plana com abas.
//
// ⚠️ AS COLUNAS "AGUARDANDO" E "PARADO HÁ" SÃO O MOTIVO DA TELA EXISTIR. Sem
// elas isto é arquivo, não fila — foi a ausência delas que deixou 12 relatórios
// de certificação esperando o gerente sem ninguém notar. O cálculo vem de
// `chamados-fila-utils.js`, ESPELHO do backend (mudou lá, muda cá).
// =====================================================================

const API = '/api/chamados';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = 'var(--ch-cor)';
const COR_ESCURA = 'var(--ch-cor-escura)';
// ⚠️ As variantes translúcidas são variáveis PRÓPRIAS: `var(--ch-cor)22` é CSS
//    inválido — sufixo de alfa não se concatena a uma função. Ver o bloco
//    "MÓDULO CHAMADOS — A COR DA FILA" no index.css.
const COR_A22 = 'var(--ch-cor-aa22)';   // o mesmo matiz, translúcido
const COR_CLARA = 'var(--ch-cor-clara)';

// Cor por STATUS — aqui a cor é SEMÂNTICA, não identidade do módulo (é a exceção
// registrada em docs/ARQUITETURA.md › Paleta dos módulos): ela diz "o que este
// número é", não "onde eu estou".
const CORES_STATUS = {
  'Aberto': { fundo: '#fef3c7', texto: '#92400e' },
  'Atribuido': { fundo: '#dbeafe', texto: '#1e40af' },
  'Em atendimento': { fundo: '#e0e7ff', texto: '#3730a3' },
  'Atendimento Pausado': { fundo: '#fed7aa', texto: '#9a3412' },
  // ⚠️ LEGADO: nome da pausa até 17/09/2026. Mesma cor, para um chamado antigo
  //    não aparecer sem selo enquanto o valor não sai do `ENUM`.
  'Aguardando loja': { fundo: '#fed7aa', texto: '#9a3412' },
  // ⚠️ `Finalizado` puxa para a MESMA FAMÍLIA de "esperando alguém de fora" da
  //    pausa (âmbar), mas mais fechado: as duas esperas são parecidas
  //    para quem olha a fila, e o que as separa é DE QUEM se espera — a coluna
  //    *Aguardando* diz isso em palavras.
  'Finalizado': { fundo: '#fde68a', texto: '#78350f' },
  'Concluido': { fundo: '#d1fae5', texto: '#065f46' },
  // ⚠️⚠️ `Encerrado` DEIXOU DE SER CINZA em 31/08: ele não é mais o fim, é uma
  //    espera (a ciência da loja). Cinza de "arquivado" faria a fila parecer
  //    resolvida justamente onde ainda falta alguém agir.
  'Encerrado': { fundo: '#cffafe', texto: '#155e75' },
  // O verde do fim: agora é o `Validado` que fecha o chamado.
  'Validado': { fundo: '#d1fae5', texto: '#065f46' },
  'Cancelado': { fundo: '#f1f5f9', texto: '#94a3b8' },
};


/**
 * O texto de "não tem nada aqui", por aba.
 *
 * ⚠️⚠️ MAPA COM FALLBACK, E NÃO TERNÁRIO — pela TERCEIRA vez neste módulo. Até
 *    01/09/2026 esta linha era
 *    `aba === 'abertos' ? 'Nenhum chamado em aberto.' : 'Nenhum chamado nesta lista.'`,
 *    e com as 6 abas da esteira CINCO delas cairiam no ramo genérico: a mensagem
 *    deixaria de dizer o que está vazio. É o mesmo bug do `rotuloDoEvento`
 *    (28/08, que rotulava toda correção como "Atendimento retomado") e do
 *    `aguardandoDe` (01/09, que faria todo reparo aparecer como Invgate).
 *
 * ⚠️ Os nomes ANTIGOS seguem aqui pelo mesmo motivo que no backend: a janela de
 *    deploy (quem não recarregou a página ainda está na aba velha).
 */
const VAZIO_DA_ABA = {
  triagem: 'Nenhum chamado aguardando triagem.',
  atribuidos: 'Nenhum chamado atribuído esperando começar.',
  atendimento: 'Nenhum chamado em atendimento.',
  'aguardando-terceiro': 'Nenhum chamado esperando terceiro (Invgate/Sonda ou compra de peças).',
  encerrados: 'Nenhum chamado encerrado.',
  cancelados: 'Nenhum chamado cancelado.',
  todos: 'Nenhum chamado registrado.',
  abertos: 'Nenhum chamado em aberto.',
  concluidos: 'Nenhum chamado nesta lista.',
};

const PRIORIDADES = ['Baixa', 'Media', 'Alta'];

// Como cada MOMENTO de foto se apresenta: título, texto alternativo e cores.
//
// ⚠️⚠️ É MAPA e não ternário porque são QUATRO momentos desde 21/09 (era ternário
//    de dois ramos, que passaria a rotular a foto do cancelamento como "O
//    problema, pela loja" — sem erro, sem log, sem sintoma). Momento novo no ENUM
//    da `dtbChamadoFoto` entra AQUI; sem isso a foto grava e ninguém a vê
//    (o rodapé de órfãs, no bloco de Evidências, é o que denuncia o esquecimento).
// ⚠️ A ORDEM DAS CHAVES é a cronologia do ato, e é ela que ordena a exibição —
//    igual à ordem de declaração do ENUM no banco, de propósito.
const MOMENTO_FOTO = {
    Abertura: {
        titulo: 'O problema, pela loja',
        alt: 'Problema relatado',
        cor: COR,
        borda: COR_CLARA,
    },
    Conclusao: {
        titulo: 'Serviço feito',
        alt: 'Serviço executado',
        cor: '#065f46',
        borda: '#86efac',
    },
    // ⚠️ Cor de CANCELAMENTO (âmbar), nem a do módulo nem a verde do serviço: a
    //    foto aqui não prova serviço feito nem problema a resolver — ela prova
    //    por que o chamado não precisava existir.
    Cancelamento: {
        titulo: 'Cancelamento',
        alt: 'Prova do cancelamento',
        cor: '#b45309',
        borda: '#fcd34d',
    },
    // ⚠️ Foto ANEXADA NA CORREÇÃO (a loja acerta o chamado em 'Aberto', ou o TI
    //    corrige depois). Cor ardósia — não é problema (a do módulo), nem serviço
    //    feito (verde), nem cancelamento (âmbar): é o anexo que faltava. Append no
    //    fim, alinhado à ordem do ENUM (ver o ⚠️ da ordem das chaves acima).
    Correcao: {
        titulo: 'Anexo da correção',
        alt: 'Foto anexada na correção',
        cor: '#475569',
        borda: '#cbd5e1',
    },
};

// Mesmo teto da abertura (1 obrigatória + 4 extras). ⚠️ O mínimo de 1 no
// encerramento vem do motor (`exigenciasPara`), não daqui.
const MAX_FOTOS = 5;
// ⚠️ O ENCERRAMENTO EXIGE 3 (a abertura, 1) — assimetria proposital: a loja
//    fotografa o que vê, o técnico registra um SERVIÇO, e com a validação da loja
//    fora do fluxo estas fotos são a única evidência de que ele aconteceu.
//    ⚠️ O mínimo também é conferido no servidor; aqui é atalho, lá é regra.
const MIN_FOTOS_ENCERRAMENTO = 3;

// ---------------------------------------------------------------------
// Os três motivos de finalização, como a TELA os chama.
//
// ⚠️⚠️ MAPA E NÃO TERNÁRIO — é o espelho da correção feita no `aguardandoDe` do
//    servidor no mesmo dia. Com dois motivos, `x === 'Pecas' ? … : …` funcionava;
//    com três, todo reparo passaria a se anunciar como Invgate, na tela e no
//    texto de espera, sem erro nenhum. Motivo novo cai no fallback ("terceiro"),
//    que é vago mas VERDADEIRO — nunca o nome de outra espera.
// ⚠️ As chaves são a grafia do ENUM (`Pecas` sem cedilha, `Reparo` sem acento);
//    o texto legível é o valor.
// ---------------------------------------------------------------------
const ROTULO_FINALIZACAO = {
    Invgate: 'Invgate',
    Pecas: 'peças a comprar',
    Reparo: 'peças em reparo',
};
const ESPERA_FINALIZACAO = {
    Invgate: 'a Sonda',
    Pecas: 'a peça chegar',
    Reparo: 'o conserto ficar pronto',
};
// ⚠️ `DIAS_PRAZO` saiu junto com o encerramento por prazo (21/08/2026): sem a
//    espera pela validação da loja, não havia mais o que destravar.

const thStyle = { padding: '10px 8px', textAlign: 'left', fontSize: 12, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 8px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
const inputStyle = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const btnAcao = (cor) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
  borderRadius: 6, border: `1px solid ${cor}`, backgroundColor: '#fff',
  color: cor, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
});

/** O mês (aaaa-mm) de abertura do chamado, em hora local.
 *
 * ⚠️ MORA NO MÓDULO, não dentro do componente: além da filtragem, quem a usa é a
 *    PODA de filtros da troca de aba (`podarFiltros`), chamada de dentro do
 *    `carregar`. Como ela não depende de estado nenhum, subir evita passá-la como
 *    dependência de `useCallback` — e garante que a poda e o filtro usem
 *    exatamente o MESMO critério de mês. Critérios diferentes podariam um período
 *    que casa, ou manteriam um que não casa.
 */
const mesDe = (c) => {
  if (!c.ABERTO_EM) return '';
  const d = new Date(c.ABERTO_EM);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');

/**
 * Data → 'AAAA-MM-DD' em hora LOCAL, para o `min` de um `<input type="date">`.
 *
 * ⚠️⚠️ NÃO É `toISOString().slice(0, 10)`, que é o atalho óbvio e está errado
 *    aqui: aquele converte para UTC, e num fuso negativo toda hora a partir das
 *    21h já é o DIA SEGUINTE. O `min` do calendário passaria a barrar o dia de
 *    hoje justamente no fim da tarde — quando o técnico está fechando o
 *    atendimento. É a mesma pegadinha que o `chamados-sla.js` documenta na chave
 *    do feriado.
 */
const diaLocalIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Duração em ms → texto curto para a coluna ("6h", "2d"). */
const fmtPrazo = (ms) => {
  if (ms == null) return null;
  const horas = Math.round(ms / 3600000);
  if (horas < 24) return `${horas}h`;
  const dias = Math.floor(horas / 24);
  const resto = horas % 24;
  return resto ? `${dias}d ${resto}h` : `${dias}d`;
};

/**
 * A COLUNA DE PRAZO da fila: texto + cor, a partir do `SLA` que o servidor manda.
 *
 * ⚠️⚠️ ISTO É APRESENTAÇÃO, NUNCA RÉGUA. Quem decide dentro/fora, qual perna
 *    está em jogo e se o relógio está pausado é o `chamados-sla.js`, no
 *    servidor (`resumoParaFila`). Recalcular qualquer pedaço aqui criaria a
 *    segunda fonte de verdade sobre o SLA — e as duas dariam respostas
 *    plausíveis, que é o tipo de divergência que ninguém percebe.
 *
 * ⚠️⚠️ O "PARADO HÁ" É A RESERVA, e não sobra do desenho antigo: chamado SEM
 *    PRIORIDADE não tem prazo (11 dos 28 em 09/09 — 40% da fila), e sem a
 *    reserva essas linhas ficariam com um travessão exatamente onde estava o
 *    único sinal que as cobra. A coluna mostra prazo quando há prazo, e o
 *    tempo parado quando não há.
 *
 * ⚠️ Chamado PAUSADO não mostra "vence em X" — o relógio dele está parado, e
 *    o texto se leria como contagem correndo (amanhã diria o mesmo, o que
 *    parece bug de cálculo). Mostra "pausado" e a PREVISÃO, que é a data que
 *    de fato se cobra ali.
 */
const leituraDoPrazo = (c, hoje) => {
  const sla = c.SLA;
  const dias = diasParado(c, hoje);
  const parado = dias === null ? null : `${dias} d parado`;

  // Backend velho (janela de deploy) não manda `SLA`. Degrada para o desenho
  // anterior em vez de mostrar coluna vazia.
  if (!sla) return { texto: parado || "—", cor: "#64748b", titulo: "Tempo desde o último ato" };

  if (sla.semPrazo) {
    return {
      texto: parado ? `sem prioridade · ${dias} d` : "sem prioridade",
      cor: "#b45309",
      titulo: "Sem triagem não existe prazo — o número é o tempo desde o último ato",
    };
  }

  if (!sla.perna) return { texto: "—", cor: "#94a3b8", titulo: "" };

  const dePerna = sla.perna === "atendimento" ? "atendimento" : "solução";

  if (sla.situacao === "fora") {
    return {
      texto: `Fora há ${fmtPrazo(sla.estouro)}`,
      cor: "#dc2626",
      titulo: `Estourou o prazo de ${dePerna}${sla.pausado ? " · relógio pausado" : ""}`,
    };
  }

  // ⚠️ Pausado vem ANTES de "vence em": a ordem importa, porque um chamado
  //    pausado e dentro do prazo cairia no ramo do "vence em" e mentiria.
  if (sla.pausado) {
    const previsao = c.PREVISAO_CONCLUSAO
      ? ` · previsão ${new Date(`${String(c.PREVISAO_CONCLUSAO).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR')}`
      : "";
    return {
      texto: `pausado${previsao}`,
      cor: "#64748b",
      titulo: `O prazo de ${dePerna} está parado enquanto o chamado espera`,
    };
  }

  if (sla.situacao === "aguardando") {
    // ⚠️ Amarelo a partir de 25% restante: é o aviso antes do estouro, que é
    //    o que dá tempo de agir. Sem ele a coluna só avisa quando não há mais
    //    o que fazer.
    const apertado = sla.restante != null && sla.restante <= 6 * 3600000;
    return {
      texto: `Vence em ${fmtPrazo(sla.restante)}`,
      cor: apertado ? "#d97706" : "#64748b",
      titulo: `Prazo de ${dePerna}`,
    };
  }

  return { texto: "No prazo", cor: "#059669", titulo: `Prazo de ${dePerna} cumprido` };
};

/**
 * Dinheiro em pt-BR. ⚠️ `null` vira travessão, NUNCA "R$ 0,00": sem cotação e
 * de graça são coisas diferentes, e a segunda subestima a compra.
 */
const fmtMoeda = (v) => (v == null || !Number.isFinite(Number(v))
  ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

// Data COM HORA, para a linha do tempo (pedido da Julia, 2026-08-27).
//
// ⚠️ NÃO substitui o `fmtData`, e a separação é o ponto. Só a linha do tempo quer
//    a hora: ali a pergunta é "quanto tempo o chamado ficou em cada mão", e dois
//    atos do MESMO DIA apareciam com a mesma data, sem ordem visível — aberto e
//    triado às 08h10 e 08h12 se liam como simultâneos. Na TABELA a hora seria
//    ruído: lá a leitura é de fila, e a coluna "Aberto em" existe para comparar
//    dias, não minutos.
// ⚠️ `DATA_AGENDADA` continua com `fmtData` mesmo dentro da linha do tempo — é um
//    `DATE`, sem hora nenhuma; formatá-la aqui inventaria "00:00" e faria a visita
//    parecer marcada para a meia-noite.
// O nome do lugar como a operação fala: 'MAL CK 3', ou só 'MAL' sem quiosque.
//
// ⚠️ ESPELHA o `nomeCompletoLoja` do servidor (`server/chamados/chamados-quiosques.js`).
//    A duplicação é a de sempre no portal — o CRA não importa do `server/` —, e o
//    risco de divergir é real: cada cópia escolheria o próprio separador, e o
//    mesmo chamado apareceria como "MAL CK 3" na tela e "MAL · CK 3" no PDF.
// ⚠️ O QUIOSQUE É SUFIXO ('CK 3'), nunca o nome inteiro: é assim que ele está
//    gravado, nas duas tabelas. Ver `migration-2026-08-27-chamados-quiosque.sql`.
const nomeDoLocal = (loja, quiosque) => (quiosque
  ? `${String(loja || '').trim()} ${String(quiosque).trim()}`
  : String(loja || '').trim());

// Como o perfil de quem valida aparece na tela.
//
// ⚠️⚠️ O FALLBACK NO FIM NÃO É DESLEIXO — é o ESCRITÓRIO. Lá não existe `Gerente`
//    nem `Gerente Plantao`, e quem valida é qualquer pessoa lotada no escritório
//    (RH, Financeiro, Contábil, TI…). Um `? :` de dois ramos rotularia todos eles
//    como "gerente de plantão", que é falso e passaria despercebido. Fora do
//    escritório o mapa cobre os dois casos possíveis.
const ROTULO_PERFIL_VALIDADOR = {
  'Gerente': 'gerente do restaurante',
  'Gerente Plantao': 'gerente de plantão',
};
const rotuloValidador = (perfil) => ROTULO_PERFIL_VALIDADOR[perfil] || perfil || '';

const fmtDataHora = (d) => (d
  ? new Date(d).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

// ---------------------------------------------------------------------
// A LINHA DO TEMPO — atos de coluna + atos repetíveis, EM ORDEM
// ---------------------------------------------------------------------
/**
 * O texto de um evento da `dtbChamadoEvento`.
 *
 * ⚠️ ESPELHA o `rotuloDoEvento` do `server/chamados/chamados-fluxo.js`, que é
 *    quem serve o PDF. A duplicação é a de sempre no portal (o CRA não importa
 *    do `server/`); o que impede a divergência de virar mentira é o `default`
 *    dos dois lados — evento novo aparece com o próprio nome nos dois, feio e
 *    igual, em vez de virar coisas diferentes em cada lugar.
 *
 * ⚠️⚠️ O `default` NÃO É DECORAÇÃO. Aqui havia um ternário de dois ramos
 *    (`Pausado ? 'pausado' : 'retomado'`), e ele estava certo enquanto só
 *    existiam esses dois eventos. Quando a CORREÇÃO entrou (28/08), esse mesmo
 *    ternário passaria a rotular toda correção como "atendimento retomado" — um
 *    erro que a tela mostraria com toda a confiança do mundo. Evento novo agora
 *    aparece com o próprio nome, feio e visível, em vez de virar outra coisa.
 */
const textoDoEvento = (ev) => {
  switch (ev.EVENTO) {
    case 'Pausado': return 'Atendimento pausado';
    case 'Retomado': return 'Atendimento retomado';
    // ⚠️ O `DETALHE` diz QUAIS campos mudaram ('tipo, equipamento'). Sem ele —
    //    correção gravada antes da migration de 28/08 — a linha continua fazendo
    //    sentido, só sem o recorte.
    case 'Corrigido': return `Chamado corrigido${ev.DETALHE ? ` (${ev.DETALHE})` : ''}`;
    // ⚠️ "Atendimento finalizado" e não "Chamado finalizado": o que terminou foi o
    //    que estava na mão do técnico — o chamado segue vivo, esperando o
    //    terceiro. ⚠️ O `DETALHE` diz esperando O QUÊ (o número do Invgate, a
    //    contagem de peças), e é ele que faz a linha do tempo se explicar sozinha.
    case 'Finalizado': return `Atendimento finalizado${ev.DETALHE ? ` (${ev.DETALHE})` : ''}`;
    // ⚠️ Aqui o `DETALHE` traz o nome de QUEM SAIU ('antes: NOME') — é a única
    //    exceção à regra "o DETALHE guarda campos, nunca valores", porque o
    //    técnico anterior é sobrescrito no banco e não está em mais lugar nenhum.
    //    O NOVO aparece na própria linha "Atribuído a …" logo acima.
    case 'Reatribuido': return `Técnico trocado${ev.DETALHE ? ` (${ev.DETALHE})` : ''}`;
    default: return ev.EVENTO || 'Alteração';
  }
};

/**
 * Monta a linha do tempo do chamado, do ato mais antigo ao mais recente.
 *
 * ⚠️⚠️ ELA PASSOU A SER ORDENADA POR DATA EM 2026-08-28, e a mudança é por
 *    NECESSIDADE, não por capricho. Antes, cada ato tinha o seu lugar fixo no
 *    JSX (aberto → triado → atribuído → iniciado → [pausas] → encerrado), e
 *    isso funcionava porque cada um só podia acontecer na sua vez: só se pausa
 *    depois de iniciar, só se encerra depois de pausar.
 *    A CORREÇÃO quebrou essa garantia — ela é permitida em qualquer estado vivo,
 *    e o caso mais comum é o mais cedo de todos: o TI acerta o tipo assim que a
 *    loja abre o chamado. Num bloco fixo entre "iniciado" e "encerrado", essa
 *    correção apareceria DEPOIS da triagem que ela precedeu. Com data ao lado,
 *    o leitor veria a contradição e desconfiaria da tela inteira.
 *
 * ⚠️ A VISITA MARCADA FICA FORA DESTA ORDENAÇÃO, de propósito: `DATA_AGENDADA`
 *    é uma data FUTURA — um compromisso, não um ato que aconteceu. Ordenada
 *    junto, ela iria parar no fim da lista, depois do encerramento. Ela é
 *    devolvida à parte para o JSX fixá-la no lugar em que faz sentido ler.
 *
 * @returns {{atos: {texto: string}[], visita: string|null}}
 */
const montarLinhaDoTempo = (d) => {
  const atos = [];
  /** Entra na linha só o que tem data — sem data não há onde ordenar. */
  const add = (quando, texto) => {
    if (!quando) return;
    const t = new Date(quando).getTime();
    if (Number.isNaN(t)) return;
    atos.push({ t, texto: `${texto} ${fmtDataHora(quando)}` });
  };

  // ⚠️ TEMPORÁRIO (janela de 10 a 18/09/2026): `ORIGEM = 'Auvo'` quer dizer que
  //    o `ABERTO_EM` foi DIGITADO na transcrição, não medido pelo relógio. Sem
  //    esta observação, a linha do tempo apresenta como fato registrado uma
  //    data que alguém informou de memória — e é dela que o SLA corre.
  add(d.ABERTO_EM, `Aberto por ${d.ABERTO_POR_NOME || '—'}`
    + `${d.ORIGEM === 'Auvo' ? ' (migrado do Auvo)' : ''} em`);
  add(d.TRIADO_EM, `Triado por ${d.TRIADO_POR_NOME || '—'} em`);
  add(d.ATRIBUIDO_EM, `Atribuído a ${d.TECNICO_NOME || '—'} em`);
  add(d.INICIADO_EM, 'Atendimento iniciado em');

  // Os atos REPETÍVEIS. ⚠️ `?? []` protege o chamado carregado antes do deploy e
  // a tabela sem GRANT: a linha do tempo perde os eventos, não quebra.
  (d.eventos ?? []).forEach((ev) => add(ev.QUANDO, `${textoDoEvento(ev)}${ev.POR_NOME ? ` por ${ev.POR_NOME}` : ''} em`));

  // ⚠️ NÃO se exibe mais o par 'Validado' × 'Prazo' no encerramento. Ele
  //    distinguia "a loja confirmou" de "ninguém respondeu em 10 dias", e a
  //    validação da loja saiu do fluxo — nada escreve mais `ENCERRAMENTO_TIPO`.
  //    Mantido o texto antigo, TODO chamado encerrado pelo técnico apareceria
  //    como "por prazo, sem confirmação", o que é falso: ele fez o serviço.
  add(d.ENCERRADO_EM, `Encerrado por ${d.ENCERRADO_POR_NOME || '—'} em`);
  // ⚠️ A validação é ATO PRÓPRIO, e não um adendo do encerramento: nos chamados
  //    antigos ela acontece dias depois, e fundir as duas datas apagaria a
  //    distância entre elas.
  add(d.VALIDADO_EM, `Validado por ${d.VALIDADO_POR_NOME || '—'} em`);
  add(
    d.CANCELADO_EM,
    `Cancelado por ${d.CANCELADO_POR_NOME || '—'}${d.MOTIVO_CANCELAMENTO ? ` — ${d.MOTIVO_CANCELAMENTO}` : ''} em`,
  );

  // ⚠️ `sort` do JS é ESTÁVEL desde o ES2019: atos com o mesmo carimbo (aberto e
  //    triado no mesmo minuto) mantêm a ordem em que foram acrescentados acima,
  //    que é a ordem da esteira. Sem isso, o desempate ficaria a critério do
  //    motor e a lista poderia trocar de ordem entre dois carregamentos.
  atos.sort((a, b) => a.t - b.t);

  return {
    atos,
    // ⚠️ Sem hora: `DATA_AGENDADA` é `DATE`. Passá-la pelo `fmtDataHora` mostraria
    //    "00:00" — uma hora que ninguém escolheu, num campo em que a hora
    //    pareceria compromisso.
    visita: d.DATA_AGENDADA ? fmtData(d.DATA_AGENDADA) : null,
  };
};

// Rótulo dos blocos do detalhe (descrição, linha do tempo, evidências). Extraído
// porque estava repetido em cada bloco — e rótulo desalinhado entre blocos vizinhos
// é o tipo de desleixo que se nota antes do conteúdo.
const rotuloBloco = {
  fontSize: 11, fontWeight: 800, color: '#94a3b8',
  margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.03em',
};

// As colunas da tabela, em ordem. `campo` é a chave de ORDENAÇÃO, e é por isso
// que a lista mora aqui em vez de estar escrita no `<thead>`: cabeçalho e regra
// de ordenação precisam concordar, e duas listas divergiriam no primeiro ajuste.
const COLUNAS = [
    { campo: 'ID', rotulo: '#' },
    { campo: 'LOJA', rotulo: 'Loja' },
    { campo: 'TITULO', rotulo: 'Assunto' },
    { campo: 'TIPO_SOLICITACAO', rotulo: 'Tipo' },
    { campo: 'EQUIPAMENTO', rotulo: 'Equipamento' },
    { campo: 'PRIORIDADE', rotulo: 'Prioridade' },
    { campo: 'STATUS', rotulo: 'Situação' },
    { campo: 'AGUARDANDO', rotulo: 'Aguardando' },
    { campo: 'SLA', rotulo: 'Prazo', direita: true },
    { campo: 'ABERTO_EM', rotulo: 'Aberto em' },
];

// ⚠️ PRIORIDADE ORDENA POR URGÊNCIA, NÃO POR ALFABETO. Alfabeticamente daria
//    Alta → Baixa → Média, que é uma ordem sem sentido nenhum para quem está
//    escolhendo o que atender primeiro.
const PESO_PRIORIDADE = { Alta: 3, Media: 2, Baixa: 1 };

// ⚠️ SITUAÇÃO ORDENA PELA ESTEIRA, não por alfabeto: "onde o chamado está no
//    fluxo" é a leitura útil. Por alfabeto, 'Encerrado' viria antes de 'Aberto'.
const PESO_STATUS = {
    // ⚠️ Os dois nomes da pausa dividem o MESMO peso (4): eles são o mesmo passo
    //    da esteira, e pesos diferentes separariam na ordenação chamados que
    //    estão exatamente no mesmo lugar do fluxo.
    'Aberto': 1, 'Atribuido': 2, 'Em atendimento': 3,
    'Atendimento Pausado': 4, 'Aguardando loja': 4,
    'Finalizado': 5, 'Concluido': 6, 'Encerrado': 7, 'Validado': 8, 'Cancelado': 9,
};

// ⚠️⚠️ O MOTIVO DA PAUSA É OBRIGATÓRIO (Julia, 17/09/2026) e este número ESPELHA o
//    `MIN_MOTIVO_PAUSA` do servidor. A trava que vale é a de lá; esta existe para
//    o técnico não descobrir a exigência num 400 depois de já ter clicado.
//    ⚠️ Mudar aqui sem mudar lá dá o pior dos dois mundos: botão liberado e
//    gravação recusada.
const MIN_MOTIVO_PAUSA = 10;

/** Este chamado está com o atendimento pausado? (o nome novo e o legado) */
const estaPausado = (status) => status === 'Atendimento Pausado' || status === 'Aguardando loja';

// ⚠️⚠️ `equipe` VEM DO HUB (a faixa de fila), e esta tela NÃO tem mais seletor
//    próprio — ele existiu por algumas horas em 17/09/2026 e saiu no mesmo dia.
//    Com a faixa global, dois controles para a mesma pergunta viram duas
//    verdades: a faixa diria "Manutenção" no topo enquanto a lista mostrava T.I,
//    e ninguém sabe qual acreditar. Um lugar só troca a fila.
const ChamadosConsulta = ({ equipe = 'TI' }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const perfil = perfilNoModulo(user?.perfil, 'chamados') || '';
  // ⚠️⚠️ ACESSO TOTAL — Admin e Diretoria (Julia, 2026-08-31). Espelha o
  //    `PERFIS_ACESSO_TOTAL` do `chamados-fluxo.js`: lá ele abre a TRANSIÇÃO, aqui
  //    só o BOTÃO. Divergir não dá erro em lugar nenhum — o botão aparece e a
  //    chamada volta 403, ou o botão falta para quem podia clicar.
  //    ⚠️ `Diretoria Op` não é citada: o `perfilNoModulo` acima já a traduziu.
  const temAcessoTotal = perfil === 'Admin' || perfil === 'Diretoria';

  // ⚠️⚠️ FASE 3 DOS ACESSOS (C′): as AÇÕES já migradas (triar/executar/validar)
  //    decidem por CAPACIDADE, não por lista de perfil. `caps` vem do mesmo
  //    resolver que o servidor usa (`/minhas-capacidades`) — é o que impede a
  //    tela de discordar da rota. A funcionalidade (`acao.*`) e a equipe
  //    (`equipe.*`) são eixos que COMPÕEM por interseção, espelho exato do
  //    `capsAutorizamPapel` do `chamados-fluxo.js`: é o que mantém o TI fora da
  //    fila de Manutenção e vice-versa, na tela como no servidor.
  const { caps } = useCapacidades();
  // A `equipe` é prop desta tela (uma instância por fila): a capacidade de
  // trabalhador correspondente é o segundo eixo da interseção.
  const capEquipe = equipe === 'Manutencao' ? 'chamados.equipe.manutencao' : 'chamados.equipe.ti';

  // Quem pode o quê. O backend é quem protege; aqui só decidimos o que aparece.
  // ⚠️ TRIAR e EXECUTAR são team-side: exigem a capacidade da AÇÃO **E** a de
  //    trabalhador da equipe deste chamado (a interseção). Quem os alcança HOJE
  //    (Gerente TI, TI) é o `padrao` de `chamados.acao.triar`/`.executar` no
  //    catálogo — mexer nesse alcance é um clique na matriz, não uma edição aqui.
  //    ⚠️ Motor, rota e tela leem a MESMA capacidade: mexer só aqui daria um botão
  //    que devolve 403/409. O acesso total (Admin/Diretoria) segue por bypass.
  const podeTriar = temAcessoTotal || (caps.has('chamados.acao.triar') && caps.has(capEquipe));
  const podeExecutar = temAcessoTotal || (caps.has('chamados.acao.executar') && caps.has(capEquipe));
  // ⚠️ Não existe mais `podeValidar`: a loja não valida encerramento (21/08/2026).
  // ⚠️ `podeCorrigir` = quem corrige A QUALQUER TEMPO (o TI, enquanto o trabalho
  //    não acabou). A LOJA também corrige desde a DEM-00009 (21/09), mas SÓ em
  //    'Aberto' (antes da triagem) — por isso o gate real é `podeCorrigirChamado`,
  //    que combina papel + estado, e não este booleano sozinho.
  const podeCorrigir = temAcessoTotal || perfil === 'Gerente TI' || perfil === 'TI';
  // Espelho do `PERFIS_ABERTURA` (loja) do `chamados-fluxo.js`. ⚠️ É só UX — o
  //    gate de verdade é a rota (`lojaPodeCorrigir` + recorte por loja); aqui só
  //    decide MOSTRAR o bloco. Um perfil a mais aqui, no máximo, mostra um botão
  //    que o backend recusa; nunca o contrário.
  const PERFIS_LOJA_CORRIGE = ['Gerente', 'Gerente Plantao', 'Coordenador de Qualidade'];
  const ehLojaQueCorrige = PERFIS_LOJA_CORRIGE.includes(perfil);
  /** Este chamado pode ser corrigido por quem está logado? (papel + estado) */
  const podeCorrigirChamado = (status) => !trabalhoAcabou(status)
    && (podeCorrigir || (ehLojaQueCorrige && status === 'Aberto'));

  // ---------------------------------------------------------------------
  // QUEM VALIDA O SERVIÇO — o ato 4, que voltou em 31/08/2026
  // ---------------------------------------------------------------------
  // ⚠️⚠️ ESTA É A ÚNICA PERMISSÃO DESTA TELA QUE **NÃO** INCLUI O ACESSO TOTAL, e
  //    é decisão, não esquecimento. Admin e Diretoria alcançam tudo no módulo —
  //    menos dar ciência em nome da loja, porque ninguém da equipe de dados nem
  //    da diretoria recebeu o serviço no balcão. Deixá-los passar encheria o
  //    histórico de ciências que não aconteceram.
  //    ⚠️ Um diretor no escritório VALIDA chamado do escritório, mas pela exceção
  //    de LOJA do backend (`ehValidadorDoEscritorio`), não por perfil — e é o
  //    servidor que decide isso, não esta linha.
  // ⚠️ `Gerente TI` fica fora apesar do nome: seria o TI atestando o próprio
  //    serviço.
  // ⚠️ Espelha `PERFIS_QUE_VALIDAM` do `chamados-validacao.js`. Aqui é só o que
  //    APARECE; quem recusa é o servidor — inclusive o gerente da loja errada,
  //    que esta linha não tem como saber.
  // ⚠️ Sob C′: a capacidade `chamados.acao.validar` (padrão = os três perfis de
  //    loja). LOJA-side: NÃO recorta por equipe (a loja é a mesma nas duas filas).
  //    Admin/Diretoria não entram porque não estão no padrão da capacidade — a
  //    exclusão do acesso total, explicada acima, virou o próprio padrão.
  const podeValidar = caps.has('chamados.acao.validar');

  // ⚠️ SÓ O GERENTE TITULAR fecha o que a Sonda resolveu (DEM-00009, frente B):
  //    retomar um 'Finalizado' e encerrar o 'Em atendimento' — mais estreito que
  //    `podeValidar` (Plantão/Coordenador validam, mas não fecham a Sonda). Aqui é
  //    só o que APARECE; a regra e o "encerrar vai direto a Validado" são do
  //    servidor (papel 'gerente-restaurante' + `encerramentoAutoValida`).
  const podeFecharSonda = perfil === 'Gerente';

  // ---------------------------------------------------------------------
  // QUEM CANCELA PELA LOJA (Julia, 2026-09-02)
  // ---------------------------------------------------------------------
  // ⚠️⚠️ É O PAPEL, NÃO O AUTOR. Até 02/09 esta tela olhava `ABERTO_POR === user.id`
  //    e só quem tinha aberto via o botão. Na loja isso não fecha: quem abre é o
  //    gerente da manhã, e quem descobre à tarde que o chamado não era para ter
  //    sido aberto é o do turno seguinte — que ficava sem saída.
  // ⚠️ Espelha `PERFIS_ABERTURA` do `chamados-fluxo.js` (lá em minúsculas). Quem
  //    recusa é o servidor — inclusive o gerente da loja errada, que esta linha
  //    não tem como saber: o recorte por loja mora no `carregarChamado` da rota.
  // ⚠️ Só vale em 'Aberto', e o `&& c.STATUS === 'Aberto'` do botão é o par desta
  //    linha: depois que o TI tocou no chamado, cancelar é dele.
  const podeCancelarPelaLoja = ['Gerente', 'Gerente Plantao', 'Coordenador de Qualidade'].includes(perfil);

  // ⚠️ CANCELAMENTO PELA EQUIPE (C′): capacidade PRÓPRIA (`chamados.acao.cancelar`),
  //    team-side (interseção com a equipe), espelho do `podeCancelar` do motor. Até
  //    aqui o botão usava `podeTriar` como proxy — por padrão dá no mesmo (as duas
  //    capacidades têm o mesmo `padrao`), mas a matriz pode conceder/negar cancelar
  //    sem mexer em triar, e aí o proxy viraria cortina.
  const podeCancelarPelaEquipe = temAcessoTotal || (caps.has('chamados.acao.cancelar') && caps.has(capEquipe));

  // ⚠️ A MAIORIA DOS USUÁRIOS DESTE MÓDULO ESTÁ NO CELULAR (Julia, 24/08/2026):
  //    o gerente abre o chamado de dentro da loja e o técnico trabalha em campo.
  //    Por isso a tela tem DOIS layouts, não um layout que encolhe.
  const { ehMobile } = useTelaMobile();
  // ⚠️ No celular os filtros nascem RECOLHIDOS: cinco seletores empilhados ocupam
  //    meia tela e empurram a lista — que é o que a pessoa veio ver — para baixo
  //    da dobra. No computador eles cabem numa linha e ficam sempre à vista.
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // ⚠️ A aba inicial é a PRIMEIRA da esteira (aguardando triagem) — é o número
  //    que o módulo existe para manter visível. Sai de `ABAS` em vez de string
  //    literal: reordenar as abas não pode deixar a tela abrindo numa gaveta que
  //    deixou de ser a primeira.
  const [aba, setAba] = useState(ABAS[0].id);

  // A contagem por aba, para o número no botão. `null` = ainda não sei (ou o
  // servidor não sabe responder).
  // ⚠️⚠️ `null` E NUNCA `{}`/zeros: a aba sem número é honesta ("não sei"), e
  //    um `0` se lê como "nada pendente" — que é exatamente a mentira que o
  //    contador existe para não contar. Backend velho devolve 404 aqui.
  const [contagem, setContagem] = useState(null);
  // Sinaliza que a PRÓXIMA carga veio de uma TROCA DE ABA e deve podar os filtros
  // impossíveis. Ver o porquê dentro do `carregar` e no `chamados-filtros.js`.
  const podarNaProximaCarga = useRef(false);

  /**
   * Troca de aba: recarrega a lista e manda podar os filtros que não casam mais.
   *
   * ⚠️ Existe como função nomeada (em vez de `onClick` solto) porque são TRÊS
   *    coisas que precisam acontecer juntas — trocar, esquecer o detalhe aberto e
   *    voltar para a página 1. Espalhadas no JSX, a próxima aba a ser criada
   *    esqueceria uma delas.
   * ⚠️ `setPagina(1)` importa: estar na página 3 da aba antiga e cair numa aba com
   *    duas páginas mostraria "nenhum registro" — o MESMO sintoma do filtro preso
   *    que estamos consertando aqui.
   */
  const trocarAba = (id) => {
    if (id === aba) return;
    podarNaProximaCarga.current = true;
    setAba(id);
    setExpandido(null);
    setPagina(1);
    // ⚠️⚠️ ABA DE UM ÚNICO STATUS ESCONDE O SELETOR DE *SITUAÇÃO* (não há o que
    //    escolher), e um filtro ESCONDIDO E ATIVO é pior que filtro nenhum: o
    //    "Limpar filtros" apareceria sem nada visível para limpar, e a contagem
    //    do cabeçalho diria "3 de 3" sem explicação. A poda do
    //    `chamados-filtros.js` não resolve este caso — ela tira o IMPOSSÍVEL, e
    //    aqui o valor é possível (só redundante).
    if ((statusDaAba(id)?.length ?? 99) <= 1) {
      setFiltros((f) => (f.status ? { ...f, status: '' } : f));
    }
  };
  const [chamados, setChamados] = useState([]);

  // --- Ordenação, filtros e paginação (pedido da Julia, 2026-08-24) ----------
  // ⚠️ TUDO NO CLIENTE, de propósito: a lista já vem inteira do servidor (são ~100
  //    chamados/mês, não 38 milhões de linhas). Levar isto para o SQL exigiria
  //    paginar no banco, e aí ordenar/filtrar passaria a custar uma ida ao servidor
  //    por clique — pior para quem usa e mais código para manter.
  const [ordem, setOrdem] = useState({ campo: 'ABERTO_EM', asc: false });
  const FILTROS_VAZIOS = { loja: '', tipo: '', prioridade: '', status: '', periodo: '' };
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [pagina, setPagina] = useState(1);

  // ⚠️ A fila desta consulta é a do HUB (prop `equipe`). O servidor assume 'TI'
  //    quando `?equipe=` não vem, então esquecer de enviá-lo mostraria a fila
  //    alheia — cheia, plausível e sem nenhum erro na tela.
  const [tecnicos, setTecnicos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [expandido, setExpandido] = useState(null);   // ID do chamado aberto
  const [detalhe, setDetalhe] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  // Rascunhos dos formulários de ação (por chamado aberto).
  const [triagem, setTriagem] = useState({ prioridade: 'Media', id_tecnico: '', tipo_solicitacao: '', data_agendada: '' });
  const [reagendar, setReagendar] = useState('');
  const [tipos, setTipos] = useState([]);
  const [conclusao, setConclusao] = useState({ observacao: '', fotos: [] });
  // O CANCELAMENTO (Julia, 09/09/2026) — motivo obrigatório + foto OPCIONAL.
  //
  // ⚠️⚠️ ERA UM `window.prompt`, e a foto é o que o aposentou: prompt nativo não
  //    aceita arquivo. A troca por painel não é enfeite — sem ela, "foto opcional
  //    no cancelamento" não tem onde acontecer.
  // ⚠️ `aberto` é campo de estado e não `motivo !== ''` (o truque usado na
  //    finalização): aqui o painel precisa poder estar aberto COM o motivo ainda
  //    vazio, que é justamente o estado inicial dele.
  const [cancelamento, setCancelamento] = useState({ aberto: false, motivo: '', fotos: [] });
  // ⚠️ DEM-00009 (frente A): a loja NÃO validou — o serviço não resolveu. Exige
  //    motivo + 1 foto do problema que voltou; abre um chamado FILHO (o pai não
  //    reabre). Mesmo formato do cancelamento (aberto/motivo/fotos).
  const [recusa, setRecusa] = useState({ aberto: false, motivo: '', fotos: [] });
  // A PAUSA DO ATENDIMENTO (Julia, 17/09/2026) — motivo obrigatório.
  //
  // ⚠️⚠️ ATÉ AQUI PAUSAR ERA UM CLIQUE SÓ. Virou bloco porque a pausa PARA O
  //    RELÓGIO DO SLA: parar o prazo sem dizer por quê é o que a exigência veio
  //    matar. Mesmo desenho do cancelamento (`aberto` em campo próprio) e pelo
  //    mesmo motivo: o bloco precisa poder estar aberto com o motivo ainda vazio.
  const [pausa, setPausa] = useState({ aberto: false, motivo: '' });
  // ---------------------------------------------------------------------
  // A FINALIZAÇÃO DO ATENDIMENTO (Julia, 31/08/2026) — agora com TRÊS saídas.
  //
  // ⚠️ `motivo` vazio = o bloco está fechado. Ele abre ao clicar em *Invgate*,
  //    *Peças a comprar* ou *Peças em reparo*, e é o motivo que decide qual campo
  //    aparece — nunca são pedidos juntos.
  // ⚠️ A lista de peças começa com UMA linha em branco: um bloco vazio com um
  //    botão "acrescentar" faz o técnico clicar duas vezes para digitar a
  //    primeira peça, que é o caso de 100% das vezes.
  //
  // ⚠️⚠️ `Reparo` ENTROU EM 2026-09-01 e NÃO é "peça a comprar com outro nome":
  //    numa se ADQUIRE, na outra o equipamento que já é nosso está na bancada de
  //    um fornecedor. Por isso o formulário dele tem só DOIS campos — produto e
  //    fornecedor. Quantidade seria sempre 1 e preço de conserto não se sabe no
  //    ato de despachar.
  // ---------------------------------------------------------------------
  /** As duas listas usam a mesma linha; o reparo só ignora quantidade e valor. */
  const PECA_VAZIA = { descricao: '', quantidade: '1', valor_unitario: '', fornecedor: '' };
  /** É um dos dois motivos que pedem lista de peças? */
  const ehMotivoDePeca = (m) => m === 'Pecas' || m === 'Reparo';
  const FINALIZACAO_VAZIA = {
    motivo: '', invgate_chamado: '', previsao_conclusao: '', pecas: [{ ...PECA_VAZIA }],
  };
  const [finalizacao, setFinalizacao] = useState(FINALIZACAO_VAZIA);
  // Rascunho da correção do TI. Campo vazio = "não mexer" — é o que permite
  // corrigir só o tipo sem reescrever a descrição da loja por acidente.
  // ⚠️ `fotos` entra na correção (DEM-00009, 21/09): a loja pode acertar o
  //    chamado em 'Aberto' e anexar a foto que faltava. Elas sobem para o
  //    SharePoint com MOMENTO='Correcao' (ver `subirFotoCorrecao`) e vão como URLs
  //    no corpo do PUT.
  const CORRECAO_VAZIA = { tipo_solicitacao: '', id_ativo: '', titulo: '', descricao: '', fotos: [] };
  const [correcao, setCorrecao] = useState(CORRECAO_VAZIA);
  // Blocos RECOLHIDOS por padrão (2026-08-24). ⚠️ `agendaManual` guarda só o
  //    que a pessoa CLICOU; o estado efetivo (`agendaAberta`) combina isso com
  //    o fato de haver ou não data. Guardar só um booleano faria o bloco nascer
  //    fechado até em chamado SEM data, que é justamente quando há o que fazer.
  // ---------------------------------------------------------------------
  // A CIÊNCIA DO GERENTE — quem valida o serviço (Julia, 2026-08-27).
  //
  // ⚠️⚠️ SUBSTITUIU A ASSINATURA DESENHADA, que durou de 24 a 27/08: em vez de
  //    digitar o nome e desenhar num canvas, escolhe-se o gerente numa LISTA e
  //    clica-se em validar — o mesmo gesto da Certificação de Equipamentos. Some
  //    o nome digitado ('joão', 'Joao G.', 'gerente') e entra o ID de uma pessoa
  //    real do cadastro.
  // ⚠️ E virou OBRIGATÓRIA: sem escolher, o chamado não encerra (o servidor
  //    recusa, pela exigência 'validacao' do `chamados-fluxo.js`).
  // ---------------------------------------------------------------------
  // ⚠️⚠️ O SELETOR DE GERENTE SAIU DA TELA EM 31/08/2026, e com ele os estados
  //    `gerentes` / `gerentesErro` / `lojaEhEscritorio` e a busca que os
  //    alimentava. Quem valida é quem está LOGADO, clicando — não há mais lista
  //    de nomes para o técnico escolher.
  //    ⚠️ A rota `GET /:id/gerentes` CONTINUA no backend de propósito, por um
  //    deploy: a tela em cache do dia anterior ainda a chama, e derrubá-la junto
  //    deixaria a janela do deploy com erro na mão do técnico. Ela sai no
  //    próximo — está registrado no card do PLANO.md.
  const [corrigirAberto, setCorrigirAberto] = useState(false);
  const [agendaManual, setAgendaManual] = useState(null);   // null = não mexeu
  // ---------------------------------------------------------------------
  // TROCAR O TÉCNICO ATRIBUÍDO (Julia, 2026-09-01)
  // ---------------------------------------------------------------------
  // ⚠️ Bloco RECOLHIDO por padrão, e aqui um booleano simples basta — ao
  //    contrário da agenda, que nasce aberta quando FALTA data. Chamado em
  //    'Atribuido' sempre TEM técnico, então não existe o caso "falta algo": a
  //    troca é exceção, e formulário aberto sugeriria que trocar é o passo
  //    seguinte.
  const [trocaAberta, setTrocaAberta] = useState(false);
  const [trocaTecnico, setTrocaTecnico] = useState('');
  // O parque, para o seletor de equipamento da correção.
  const [ativos, setAtivos] = useState([]);

  /** Há alguma coisa preenchida na correção? (o botão só liga com algo a mudar)
   *  ⚠️ Foto conta: uma correção pode ser SÓ a foto que faltava, sem tocar texto. */
  const algoACorrigir = () => correcao.fotos.length > 0
    || ['tipo_solicitacao', 'id_ativo', 'titulo', 'descricao']
      .some((k) => String(correcao[k] || '').trim() !== '');

  /**
   * O botão "Encerrar" pode ligar?
   *
   * ⚠️ DUAS exigências, as MESMAS do servidor (`exigenciasPara('Encerrado')` →
   *    observacao · foto). Dizê-las aqui é atalho para o técnico não levar um 400
   *    depois de dar o serviço por terminado. A regra continua sendo a de lá.
   *
   * ⚠️⚠️ A TERCEIRA EXIGÊNCIA — a ciência do gerente — SAIU EM 31/08/2026. Não
   *    porque deixou de importar: ela virou ATO PRÓPRIO depois do encerramento,
   *    com o gerente clicando no próprio login. Enquanto morava aqui, o técnico
   *    escolhia o nome no aparelho dele (declaração, não prova) e ainda ficava
   *    preso quando não havia gerente por perto.
   */
  const podeEncerrar = () => conclusao.observacao.trim()
    && conclusao.fotos.length >= MIN_FOTOS_ENCERRAMENTO;

  /**
   * O botão "Finalizar atendimento" pode ligar?
   *
   * ⚠️ Espelha o `validarFinalizacao` do servidor (`chamados-finalizacao.js`):
   *    Invgate exige o NÚMERO; peças exigem ao menos uma linha com descrição e
   *    quantidade > 0. O valor é OPCIONAL de propósito — o técnico finaliza na
   *    loja, muitas vezes antes de ter cotação, e exigir o número faria ele
   *    inventar um.
   */
  const podeFinalizar = () => {
    // ⚠️⚠️ A PREVISÃO É PRÉ-REQUISITO DE TODOS OS MOTIVOS (SLA, 09/09), e vem
    //    antes de qualquer regra de conteúdo: finalizar PAUSA o relógio do prazo,
    //    e parar o relógio sem uma data prometida seria um 'adiar
    //    indefinidamente' sem nada a cobrar depois. O servidor recusa igual
    //    (`validarPrevisao`) — isto aqui só evita o clique que volta 400.
    if (!finalizacao.previsao_conclusao) return false;
    if (finalizacao.motivo === 'Invgate') return !!finalizacao.invgate_chamado.trim();
    if (finalizacao.motivo === 'Pecas') {
      // ⚠️⚠️ O FORNECEDOR SAIU DA CONTA EM 2026-09-01, revertendo a exigência de
      //    31/08: quem sabe de quem comprar é o Gerente de TI, no escritório, e
      //    não o técnico de pé na loja. Ele preenche depois, pelo painel de
      //    peças. Aqui sobram produto e quantidade — o que faz a linha ser peça.
      return finalizacao.pecas.some(
        (p) => p.descricao.trim()
          && Number(String(p.quantidade).replace(',', '.')) > 0,
      );
    }
    // ⚠️ No REPARO o fornecedor é obrigatório, e a assimetria com a compra é a
    //    decisão: aqui ele já foi escolhido — a peça está fisicamente com ele
    //    quando o técnico registra. Sem o nome, ninguém sabe onde ela está.
    if (finalizacao.motivo === 'Reparo') {
      return finalizacao.pecas.some((p) => p.descricao.trim() && p.fornecedor.trim());
    }
    return false;
  };

  /** O corpo do PUT de finalizar, já sem as linhas de peça em branco. */
  const corpoDaFinalizacao = () => (finalizacao.motivo === 'Invgate'
    ? {
      motivo: 'Invgate',
      invgate_chamado: finalizacao.invgate_chamado.trim(),
      previsao_conclusao: finalizacao.previsao_conclusao,
    }
    // ⚠️ Descarta a linha vazia que sempre sobra no fim: o técnico acrescenta uma
    //    peça, desiste da última e mandaria uma linha em branco que o servidor
    //    recusaria nomeando "Peça 3" — um erro sobre algo que ele não digitou.
    // ⚠️ O motivo vai como está (`Pecas` ou `Reparo`) — é ele que diz ao servidor
    //    qual regra aplicar e que `TIPO` carimbar em cada linha.
    : {
      motivo: finalizacao.motivo,
      previsao_conclusao: finalizacao.previsao_conclusao,
      pecas: finalizacao.pecas.filter((p) => p.descricao.trim()),
    });

  /** Edita UM campo de UMA peça, sem remontar as outras. */
  const mudarPeca = (idx, campo, valor) => setFinalizacao((f) => ({
    ...f,
    pecas: f.pecas.map((p, i) => (i === idx ? { ...p, [campo]: valor } : p)),
  }));

  const acrescentarPeca = () => setFinalizacao((f) => ({
    ...f, pecas: [...f.pecas, { ...PECA_VAZIA }],
  }));

  /** ⚠️ Nunca deixa a lista vazia — ver o comentário do botão de remover. */
  const removerPeca = (idx) => setFinalizacao((f) => (f.pecas.length === 1 ? f : {
    ...f, pecas: f.pecas.filter((_, i) => i !== idx),
  }));

  /**
   * Soma do orçamento à vista. `null` quando NENHUMA peça tem valor.
   *
   * ⚠️⚠️ ESPELHA o `totalDasPecas` do servidor (`chamados-finalizacao.js`), e o
   *    `null` é a parte que importa: "R$ 0,00" se lê como "não custa nada", e o
   *    que se quer dizer é "ainda não sabemos". Zerar aqui faria a tela anunciar
   *    um orçamento gratuito.
   */
  const totalDoOrcamento = () => {
    const num = (v) => {
      const n = Number(String(v ?? '').trim().replace(/\s/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    };
    // ⚠️ Só a COMPRA tem orçamento. No reparo não há valor nem quantidade, e
    //    somá-lo o faria entrar como zero — "R$ 0,00 neste conserto", que se lê
    //    como de graça. Espelha o filtro por TIPO do `totalDasPecas` do servidor.
    if (finalizacao.motivo !== 'Pecas') return null;
    const linhas = finalizacao.pecas.filter((x) => x.descricao.trim());
    if (!linhas.some((x) => String(x.valor_unitario).trim() && num(x.valor_unitario) != null)) return null;
    return linhas.reduce((soma, x) => soma + (num(x.valor_unitario) || 0) * (num(x.quantidade) || 0), 0);
  };

  /**
   * Monta o corpo do PUT com SÓ o que foi preenchido.
   * ⚠️ Campo ausente ≠ campo vazio: mandar `titulo: ''` faria o servidor recusar
   *    ("não pode ficar vazio"); não mandar a chave é o que significa "não mexer".
   */
  const corpoDaCorrecao = () => {
    const corpo = {};
    if (correcao.tipo_solicitacao) corpo.tipo_solicitacao = correcao.tipo_solicitacao;
    if (correcao.titulo.trim()) corpo.titulo = correcao.titulo.trim();
    if (correcao.descricao.trim()) corpo.descricao = correcao.descricao.trim();
    // O sentinela `__limpar__` é o "tirar o equipamento": vira string vazia, que o
    // servidor entende como `ID_ATIVO = NULL`.
    if (correcao.id_ativo === '__limpar__') corpo.id_ativo = '';
    else if (correcao.id_ativo) corpo.id_ativo = correcao.id_ativo;
    // ⚠️ Manda as URLs (string), não os objetos `{url, nome}` do estado — o mesmo
    //    `.map((f) => f.url)` da conclusão e do cancelamento. O backend faz
    //    `String(url)`, então objeto viraria '[object Object]'.
    if (correcao.fotos.length) corpo.fotos = correcao.fotos.map((f) => f.url);
    return corpo;
  };

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API}?aba=${aba}&equipe=${encodeURIComponent(equipe)}`, { headers: auth() });
      if (!r.ok) throw new Error('Falha ao carregar os chamados.');
      // ⚠️⚠️ ESTREITA DE NOVO NO CLIENTE, e não é paranoia: é a rede contra a
      //    JANELA DE DEPLOY. O `build/` sobe antes do `pm2 reload`, então por
      //    alguns segundos esta tela nova conversa com o backend ANTIGO — que não
      //    conhece `?aba=triagem`, não casa no `if/else` de lá e devolve TUDO SEM
      //    FILTRO de status. Sem esta linha, *Em aberto* mostraria validado e
      //    cancelado misturados, sem erro nenhum e sem sintoma que aponte a causa.
      // ⚠️ Em regime normal ela não tira nada — é o pretendido. Não "simplificar".
      const lista = filtrarPelaAba(await r.json(), aba);
      setChamados(lista);

      // ⚠️⚠️ A PODA DOS FILTROS ACONTECE **SÓ NA TROCA DE ABA** (Julia, 2026-08-27),
      //    e é por isso que existe o sinalizador em vez de podar sempre aqui.
      //    `carregar()` roda também depois de CADA ação (triar, encerrar, cancelar),
      //    e podar nessas horas apagaria o filtro da pessoa no meio do trabalho:
      //    encerrar o último chamado "Em atendimento" com esse filtro ligado faria
      //    a lista inteira reaparecer de repente, sem ela ter tocado em nada.
      //    Na troca de aba o filtro impossível é ruído; durante o trabalho, é a
      //    escolha dela.
      // ⚠️ É `ref` e não estado: mexer nele não pode disparar render, e ele tem de
      //    sobreviver ao render entre o clique na aba e a resposta do servidor.
      if (podarNaProximaCarga.current) {
        podarNaProximaCarga.current = false;
        setFiltros((f) => podarFiltros(f, lista, mesDe));
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }

    // A contagem das abas, para o número no botão.
    //
    // ⚠️⚠️ EM `try` PRÓPRIO, DEPOIS DA LISTA: contador é enfeite útil, lista é a
    //    tela. Junto no `try` de cima, um 404 aqui (backend velho) faria a
    //    Consulta inteira exibir "Falha ao carregar os chamados" tendo a lista
    //    chegado bem — erro que manda procurar defeito onde não há.
    // ⚠️ Roda a CADA `carregar()`, não só na troca de aba: triar, encerrar ou
    //    cancelar muda a contagem das OUTRAS abas, e número velho no botão é
    //    pior que número nenhum.
    try {
      // ⚠️ A contagem leva a MESMA equipe da lista. Sem isso, os números dos
      //    botões seriam os da fila de TI enquanto a lista mostra a de Manutenção
      //    — e o contador é o que faz a aba que ninguém abre existir.
      const rc = await fetch(`${API}/contagem?equipe=${encodeURIComponent(equipe)}`, { headers: auth() });
      // ⚠️ Falha vira `null` (aba sem número), JAMAIS zeros.
      setContagem(rc.ok ? contagemPorAba(await rc.json()) : null);
    } catch {
      setContagem(null);
    }
    // ⚠️ `equipe` na lista: trocar de fila na faixa tem de recarregar. Sem ela, o
    //    topo diria "Manutenção" e a lista seguiria sendo a de T.I.
  }, [aba, equipe]);

  useEffect(() => { carregar(); }, [carregar]);

  // Esc fecha o modal. ⚠️ O listener só existe ENQUANTO o modal está aberto: um
  // listener permanente capturaria Esc de qualquer outra coisa da tela.
  useEffect(() => {
    if (expandido === null) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape') fecharDetalhe(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [expandido]);

  // ⚠️ Trava a rolagem do FUNDO enquanto o modal está aberto — senão a roda do
  // mouse rola a tabela atrás dele, e ao fechar a lista está em outro lugar.
  useEffect(() => {
    if (expandido === null) return undefined;
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = antes; };
  }, [expandido]);

  useEffect(() => {
    // ⚠️ Técnicos, Gerente TI e — desde a DEM-00009 — a LOJA (em 'Aberto')
    //    corrigem chamado, então todos precisam das listas de tipo e equipamento.
    //    Condicionar só a `podeTriar` deixava o seletor da correção vazio, e vazio
    //    se lê como "não há opção". (`/tecnicos` nega a loja com 403, mas o
    //    fallback é lista vazia e a loja não tria — sem prejuízo.)
    if (!podeTriar && !podeCorrigir && !ehLojaQueCorrige) return;
    fetch(`${API}/tecnicos`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setTecnicos)
      .catch(() => setTecnicos([]));
    // Os tipos vêm da API (sem cópia no front) — a triagem pode corrigir o que a
    // loja escolheu errado.
    fetch(`${API}/tipos-solicitacao`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setTipos)
      .catch(() => setTipos([]));
    // O parque alimenta o seletor de equipamento da correção.
    fetch('/api/chamados/ativos?ativos=1', { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAtivos)
      .catch(() => setAtivos([]));
  }, [podeTriar, podeCorrigir, ehLojaQueCorrige]);

  /**
   * Entrega o PDF do chamado — baixando no celular, abrindo em aba no computador.
   *
   * ⚠️ Busca com `fetch` e usa o BLOB, em vez de um `<a href>` simples: a rota
   *    exige o token no cabeçalho `Authorization`, e um link comum não o envia —
   *    a aba abriria em 401. É o mesmo caminho do `ImagemSharePoint`.
   *
   * ⚠️⚠️ SÃO DOIS CAMINHOS, E O QUE OS SEPARA É O CELULAR (achado em
   *    2026-08-24, testando no aparelho: o botão não entregava nada). Abrir aba
   *    nova só é permitido enquanto o gesto do toque ainda vale, e a geração do
   *    PDF (Puppeteer + fotos do SharePoint) leva segundos — quando o
   *    `window.open` finalmente roda, o crédito do toque já expirou. O Chrome do
   *    computador releva; Safari/iOS e Chrome/Android bloqueiam, e EM SILÊNCIO.
   *    `<a download>` não é tratado como popup e passa. No aparelho o arquivo cai
   *    na folha de compartilhamento — de onde o técnico manda no WhatsApp, que é
   *    o uso real na loja.
   * ⚠️ No computador segue o `window.open`, que dá o visualizador nativo do PDF
   *    (zoom, imprimir, salvar) de graça — mas SEM `noopener`: com ele o contexto
   *    novo se desliga da origem que criou o blob, e Safari/Firefox recusam
   *    carregar `blob:` ali, abrindo uma aba em branco.
   * ⚠️ A URL do blob é liberada depois de um tempo, não na hora: revogar
   *    imediatamente fecharia o PDF antes de o navegador terminar de renderizá-lo.
   */
  const abrirPdf = async (id) => {
    setOcupado(true); setErro('');
    try {
      const r = await fetch(`${API}/${id}/pdf`, { headers: auth() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || 'Não foi possível gerar o PDF.');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (ehMobile) {
        // ⚠️ O `<a>` entra no documento antes do clique: o Firefox ignora clique
        //    programático em elemento solto na memória.
        const a = document.createElement('a');
        a.href = url;
        a.download = `Chamado_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setErro(e.message);
    } finally {
      setOcupado(false);
    }
  };

  /** Fecha o modal e descarta os rascunhos não salvos. */
  const fecharDetalhe = () => {
    setExpandido(null);
    setDetalhe(null);
    // ⚠️ O erro morre junto: ele nasceu de um ato dentro do modal, e sem isto
    //    reapareceria no banner da página, solto, depois de fechar a janela.
    setErro('');
  };

  const abrirDetalhe = async (id) => {
    // ⚠️ Com modal, clicar na linha SEMPRE abre — o fechamento tem os seus três
    //    caminhos próprios (X, Esc, fundo). Manter o toggle aqui faria a linha de
    //    trás, visível atrás do modal, funcionar como um segundo botão de fechar.
    setExpandido(id); setDetalhe(null);
    setTriagem({ prioridade: 'Media', id_tecnico: '', tipo_solicitacao: '', data_agendada: '' });
    setReagendar('');
    setConclusao({ observacao: '', fotos: [] });
    // ⚠️ Zera junto com os demais: motivo de cancelamento vazado de um chamado
    //    para outro seria gravado como a justificativa do chamado errado — e o
    //    cancelamento é FINAL, então não há como corrigir depois.
    setCancelamento({ aberto: false, motivo: '', fotos: [] });
    // ⚠️ Zera junto pelo mesmo motivo do cancelamento: motivo de pausa vazado de
    //    um chamado para outro explicaria a parada do chamado errado — e é ele
    //    que a loja lê na fila enquanto o serviço não anda.
    setPausa({ aberto: false, motivo: '' });
    setCorrecao(CORRECAO_VAZIA);
    // ⚠️ Recusa zera junto: motivo/foto vazados abririam o chamado filho errado.
    setRecusa({ aberto: false, motivo: '', fotos: [] });
    // ⚠️ A finalização também zera ao trocar de chamado: rascunho de peça vazado
    //    de um chamado para outro entraria no orçamento errado.
    setFinalizacao(FINALIZACAO_VAZIA);
    setCorrigirAberto(false);
    setAgendaManual(null);
    // ⚠️ Zera junto: técnico escolhido e não confirmado num chamado vazaria para
    //    o próximo, e ali o clique trocaria o dono de OUTRO chamado.
    setTrocaAberta(false);
    setTrocaTecnico('');
    // ⚠️ Zera a trava ANTES de buscar: sem isso o autosave do chamado anterior
    //    ainda estaria armado e gravaria o formulário em branco por cima do
    //    rascunho deste, antes mesmo de ele chegar.
    setRascunhoFinalLido(false);
    try {
      const r = await fetch(`${API}/${id}`, { headers: auth() });
      if (!r.ok) throw new Error('Falha ao carregar o chamado.');
      setDetalhe(await r.json());
      // ⚠️ O rascunho vem DEPOIS do detalhe e em chamada própria: se ele falhar,
      //    o chamado abre normalmente — perder o rascunho custa digitação, não
      //    poder abrir o chamado custa o atendimento.
      const rr = await fetch(`${API}/rascunho?contexto=finalizacao&chamado=${id}`, { headers: auth() })
        .then((x) => (x.ok ? x.json() : { rascunho: null }))
        .catch(() => ({ rascunho: null }));
      if (rr.rascunho && rr.rascunho.motivo) {
        // ⚠️ Cada campo com o seu fallback: rascunho de uma versão anterior da
        //    tela não pode gravar `undefined` num input controlado.
        setFinalizacao({
          motivo: rr.rascunho.motivo || '',
          invgate_chamado: rr.rascunho.invgate_chamado || '',
          // ⚠️ Rascunho GRAVADO ANTES do campo existir (09/09) não tem a chave, e
          //    o `|| ''` é o que evita `undefined` num input controlado. Mas
          //    atenção: uma previsão rascunhada semana passada pode já estar no
          //    PASSADO — o botão fica travado e o servidor recusaria. É aceito de
          //    propósito: a data tem de ser reescolhida, que é o certo.
          previsao_conclusao: rr.rascunho.previsao_conclusao || '',
          pecas: Array.isArray(rr.rascunho.pecas) && rr.rascunho.pecas.length
            ? rr.rascunho.pecas.map((x) => ({ ...PECA_VAZIA, ...x }))
            : [{ ...PECA_VAZIA }],
        });
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      // ⚠️ SEMPRE arma o autosave, mesmo se a leitura falhou: sem isto, um erro
      //    de rede ao abrir deixaria o formulário sem salvar rascunho pelo resto
      //    da sessão — e em silêncio, que é o pior jeito de perder digitação.
      setRascunhoFinalLido(true);
    }

    // ⚠️⚠️ AQUI SE BUSCAVA A LISTA DE GERENTES para o seletor da ciência, e a
    //    busca saiu em 31/08/2026 junto com o seletor: quem valida é quem está
    //    LOGADO, clicando no próprio botão — não há mais nome para escolher.
    //    ⚠️ Uma chamada a menos por chamado aberto, e some o modo de falha que o
    //    comentário antigo tratava (lista vazia travando o encerramento): encerrar
    //    não depende mais de haver gerente cadastrado.
  };

  // ---------------------------------------------------------------------
  // O RASCUNHO DA FINALIZAÇÃO (Julia, 2026-09-01) — no padrão da Certificação.
  //
  // ⚠️⚠️ O QUE ELE CONSERTA: a lista de peças é digitada NA LOJA, no celular,
  //    com o equipamento aberto. Sete linhas de produto, quantidade e valor e um
  //    toque fora do modal apagava tudo — o modal fecha, o `FINALIZACAO_VAZIA`
  //    entra no lugar, e não havia nada a recuperar.
  //
  // ⚠️⚠️ SALVAR RASCUNHO NÃO MOVE O CHAMADO. O rascunho vive na
  //    `dtbChamadoRascunho`, não em coluna da `dtbChamado`: abrir o painel e
  //    digitar meia peça não finaliza, não pausa e não muda status nenhum. Se um
  //    dia isso passar por um `PUT /:id/...`, a esteira anda sozinha.
  //
  // ⚠️ É POR CHAMADO: o rascunho pende do `ID`. Sem isso, peça digitada num
  //    chamado apareceria no orçamento de outro — o mesmo vazamento que o
  //    `setFinalizacao(FINALIZACAO_VAZIA)` do `abrirDetalhe` já evitava em
  //    memória, e que voltaria pelo banco.
  // ---------------------------------------------------------------------
  const [rascunhoFinalLido, setRascunhoFinalLido] = useState(false);

  // ⚠️ Só os campos do formulário entram no snapshot — nada que venha da API,
  //    senão o autosave dispararia sozinho ao carregar.
  const snapshotFinalizacao = JSON.stringify(finalizacao);

  const salvarRascunhoFinal = useCallback(async () => {
    const r = await fetch(`${API}/rascunho`, {
      method: 'PUT',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contexto: 'finalizacao', id_chamado: expandido, conteudo: JSON.parse(snapshotFinalizacao),
      }),
    });
    if (!r.ok) throw new Error('Não foi possível salvar o rascunho.');
  }, [snapshotFinalizacao, expandido]);

  // ⚠️⚠️ `ativo` exige TRÊS coisas, e a terceira é a que evita lixo: só grava
  //    depois de ler (senão apaga o que veio), com um chamado aberto, e com um
  //    MOTIVO escolhido. Sem a última, abrir o modal de qualquer chamado
  //    gravaria um rascunho vazio para cada um deles.
  const { estado: autosaveFinal } = useAutosaveRascunho({
    ativo: rascunhoFinalLido && !!expandido && !!finalizacao.motivo && !ocupado,
    snapshot: snapshotFinalizacao,
    salvarFn: salvarRascunhoFinal,
  });

  /** Chama a API e recarrega. Centraliza o tratamento de erro das 6 ações. */
  const agir = async (caminho, corpo, mensagemOk) => {
    setOcupado(true); setErro(''); setAviso('');
    try {
      const r = await fetch(`${API}${caminho}`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo || {}),
      });
      const dados = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(dados.message || 'Não foi possível concluir a ação.');
      setAviso(mensagemOk);
      setExpandido(null); setDetalhe(null);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setOcupado(false);
    }
  };

  /**
   * Sobe UMA foto e devolve `{ url, nome }`, ou `null` se falhou.
   *
   * ⚠️ UMA função para os dois momentos que a tela sobe (conclusão e
   *    cancelamento), e é de propósito: a segunda cópia divergiria da primeira no
   *    primeiro ajuste — e o que muda entre elas é só o `momento` e onde a URL é
   *    guardada. Quem chama decide o destino.
   * ⚠️ Devolve `null` em vez de lançar: o erro já foi para a tela pelo `setErro`,
   *    e quem chama só precisa saber se tem URL para guardar.
   */
  const subirFoto = async (arquivo, loja, momento) => {
    if (!arquivo) return null;
    setOcupado(true); setErro('');
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('momento', momento);
      fd.append('loja', loja || '');
      const r = await fetch(`${API}/upload-foto`, { method: 'POST', headers: auth(), body: fd });
      const dados = await r.json();
      if (!r.ok) throw new Error(dados.message || 'Falha ao enviar a imagem.');
      return { url: dados.url, nome: arquivo.name };
    } catch (e) {
      setErro(e.message);
      return null;
    } finally {
      setOcupado(false);
    }
  };

  const subirFotoConclusao = async (arquivo, loja) => {
    const f = await subirFoto(arquivo, loja, 'Conclusao');
    if (f) setConclusao((c) => ({ ...c, fotos: [...c.fotos, f] }));
  };

  const subirFotoCancelamento = async (arquivo, loja) => {
    const f = await subirFoto(arquivo, loja, 'Cancelamento');
    if (f) setCancelamento((c) => ({ ...c, fotos: [...c.fotos, f] }));
  };

  const subirFotoCorrecao = async (arquivo, loja) => {
    const f = await subirFoto(arquivo, loja, 'Correcao');
    if (f) setCorrecao((x) => ({ ...x, fotos: [...x.fotos, f] }));
  };

  // A foto da recusa é o problema que VOLTOU — entra como Abertura do chamado
  // FILHO (o backend a grava com MOMENTO='Abertura' no filho).
  const subirFotoRecusa = async (arquivo, loja) => {
    const f = await subirFoto(arquivo, loja, 'Abertura');
    if (f) setRecusa((x) => ({ ...x, fotos: [...x.fotos, f] }));
  };

  /**
   * Sobe VÁRIAS fotos de uma vez (o gatilho de galeria do EscolherAnexo).
   *
   * ⚠️ O teto (`restante`) é calculado por QUEM CHAMA e fatiado aqui, uma vez: o
   *    upload é em série (o backend é `upload.single`), e ler o tamanho do estado
   *    dentro do laço traria valor velho (setState é assíncrono). Avisa o que
   *    sobrou em vez de engolir em silêncio.
   * @param {File[]}   arquivos  o lote escolhido
   * @param {object}   cfg       { momento, loja, restante, append }
   */
  const anexarFotosLote = async (arquivos, { momento, loja, restante, append }) => {
    if (restante <= 0) { setErro(`Máximo de ${MAX_FOTOS} fotos.`); return; }
    if (arquivos.length > restante) {
      setErro(`Você pode anexar mais ${restante} — as demais foram ignoradas.`);
    }
    for (const a of arquivos.slice(0, restante)) {
      const f = await subirFoto(a, loja, momento); // eslint-disable-line no-await-in-loop -- upload single; em série mantém ordem e não estoura o SharePoint
      if (f) append(f);
    }
  };

  const hoje = new Date();

  // -------------------------------------------------------------------------
  // ORDENAÇÃO
  // -------------------------------------------------------------------------
  /**
   * Clique no cabeçalho: mesma coluna inverte o sentido; outra começa do zero.
   *
   * ⚠️ `semAlternar` existe para o SELETOR do celular: lá, escolher no `<select>`
   *    o campo que já está ativo é reafirmar a escolha, nunca pedir para inverter —
   *    inverter ali faria a lista virar de cabeça para baixo sem o usuário ter
   *    pedido. No celular, quem inverte é o botão da seta, ao lado.
   */
  const ordenarPor = (campo, semAlternar = false) => {
    setOrdem((o) => (o.campo === campo && !semAlternar ? { campo, asc: !o.asc } : { campo, asc: o.campo === campo ? o.asc : true }));
    setPagina(1);   // ⚠️ Reordenar na página 3 mostraria o meio da lista nova.
  };

  /** O valor de comparação de cada coluna. */
  const valorDe = (c, campo) => {
    switch (campo) {
        case 'PRIORIDADE': return PESO_PRIORIDADE[c.PRIORIDADE] || 0;
        case 'STATUS': return PESO_STATUS[c.STATUS] || 99;
        case 'SLA': {
            // ⚠️⚠️ ORDENA POR URGÊNCIA, e o critério não é o texto da coluna:
            //    ordenar "Fora há 6h" / "Vence em 4h" / "pausado" alfabeticamente
            //    daria uma lista sem sentido nenhum para quem escolhe o que
            //    atender primeiro. A escala é a MESMA dos números do SLA (ms),
            //    com o estouro POSITIVO e o que ainda falta NEGATIVO — assim uma
            //    ordenação só põe o mais estourado num extremo e o mais folgado
            //    no outro.
            const sla = c.SLA;
            if (!sla) return Number.NEGATIVE_INFINITY;
            // ⚠️ Sem prioridade vai para o EXTREMO, com o tempo parado como peso:
            //    são os que ninguém triou, e é a fila do Gerente de TI. Enfiá-los
            //    no meio da escala de prazo os esconderia entre os que têm prazo.
            if (sla.semPrazo) {
                const d = diasParado(c, hoje);
                return Number.MAX_SAFE_INTEGER - (d === null ? 0 : d);
            }
            if (sla.situacao === "fora") return sla.estouro ?? 0;
            if (sla.restante != null) return -sla.restante;
            return Number.NEGATIVE_INFINITY;
        }
        case 'EQUIPAMENTO': return c.EQUIPAMENTO_DESCRICAO || '';
        case 'AGUARDANDO': return aguardandoDe(c) || '';
        case 'ABERTO_EM': return c.ABERTO_EM ? new Date(c.ABERTO_EM).getTime() : 0;
        case 'ID': return Number(c.ID) || 0;
        default: return c[campo] ?? '';
    }
  };

  const ordenados = [...chamados].sort((a, b) => {
    const va = valorDe(a, ordem.campo);
    const vb = valorDe(b, ordem.campo);
    let cmp;
    if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
    // ⚠️ `localeCompare` com 'pt-BR': sem ele, 'Ó' vem depois de 'Z' e a coluna
    //    de loja/assunto ordena errado para quem lê.
    else cmp = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
    // Empate desfeito pelo ID (decrescente): sem isso a ordem "empatada" muda
    // entre renders e a tabela pisca.
    if (cmp === 0) return Number(b.ID) - Number(a.ID);
    return ordem.asc ? cmp : -cmp;
  });

  // -------------------------------------------------------------------------
  // FILTROS — loja, tipo, prioridade, situação e período (aaaa-mm)
  // -------------------------------------------------------------------------
  // As opções saem do que EXISTE na lista carregada, não de listas fixas: filtro
  // que oferece opção sem resultado nenhum faz a pessoa duvidar da tela.
  // ⚠️ Exceção: `tipo` e `prioridade` usam as listas canônicas quando disponíveis,
  //    porque são vocabulário do módulo — mas caem para o observado se a API não
  //    tiver respondido ainda.
  const opcoes = (campo) => [...new Set(chamados.map((c) => c[campo]).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));

  // As opções do filtro de TIPO = a lista da API ∪ os tipos presentes na lista
  // carregada.
  //
  // ⚠️⚠️ A UNIÃO É O CONSERTO DE UM BUG DE INVISIBILIDADE, não um refinamento.
  //    Isto era `tipos.length ? tipos : opcoes('TIPO_SOLICITACAO')` — ou seja,
  //    quando a API respondia (o caso normal), os valores observados eram
  //    DESCARTADOS. Como a lista de tipos encolheu de 5 para 3 e o passado ficou
  //    intacto de propósito, os 22 chamados gravados como "Solicitação de
  //    Reparo" apareciam na tabela e **nenhuma opção do filtro os isolava**.
  //    Sem erro, sem aviso: só um filtro que não alcança metade da base.
  //
  // ⚠️ A ordem é deliberada: primeiro os tipos ATUAIS na ordem da API (que é a
  //    frequência medida, não alfabética), depois os legados que sobraram. Um
  //    `.sort()` no fim jogaria "Instalação" na frente do tipo que responde por
  //    82% dos chamados.
  //
  // ⚠️ O legado entra só se ESTIVER na lista carregada — não é uma lista fixa de
  //    nomes antigos. É a mesma filosofia da poda do `chamados-filtros.js`: a
  //    aba só oferece o filtro que tem como casar ali. Numa aba sem chamado
  //    legado, a opção não deve existir.
  const tiposDoFiltro = (() => {
    const observados = opcoes('TIPO_SOLICITACAO');
    if (!tipos.length) return observados;
    return [...tipos, ...observados.filter((t) => !tipos.includes(t))];
  })();

  const mesesDisponiveis = [...new Set(chamados.map(mesDe).filter(Boolean))].sort().reverse();

  const filtrados = ordenados.filter((c) => {
    if (filtros.loja && c.LOJA !== filtros.loja) return false;
    if (filtros.tipo && c.TIPO_SOLICITACAO !== filtros.tipo) return false;
    // ⚠️ 'sem' é opção de verdade: chamado ainda não triado NÃO tem prioridade, e
    //    "sem prioridade" é justamente a fila que interessa ao Gerente TI.
    if (filtros.prioridade === 'sem' && c.PRIORIDADE) return false;
    if (filtros.prioridade && filtros.prioridade !== 'sem' && c.PRIORIDADE !== filtros.prioridade) return false;
    if (filtros.status && c.STATUS !== filtros.status) return false;
    if (filtros.periodo && mesDe(c) !== filtros.periodo) return false;
    return true;
  });

  const filtroAtivo = Object.values(filtros).some(Boolean);
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  // ⚠️ Se o filtro encurtou a lista e a página atual não existe mais, cai na 1 —
  //    senão a tela mostra "página 4 de 1" e nenhuma linha.
  const paginaValida = Math.min(pagina, totalPaginas);
  const daPagina = filtrados.slice((paginaValida - 1) * POR_PAGINA, paginaValida * POR_PAGINA);

  const mudarFiltro = (campo, valor) => {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPagina(1);
    setExpandido(null);   // o detalhe aberto pode não estar mais na lista
  };

  // ⚠️⚠️ ESTAS DUAS SÃO FUNÇÕES QUE DEVOLVEM JSX, **NÃO COMPONENTES** — e isso é
  //    o conserto de um bug real, não estilo.
  //
  //    Como componentes declarados DENTRO deste componente (`const X = () => ...`
  //    chamado como `<X />`), a cada re-render do pai eles viravam uma FUNÇÃO NOVA.
  //    O React compara tipo por identidade: função nova = outro tipo de componente
  //    = **desmonta e remonta** a subárvore inteira. Consequência medida em
  //    21/08/2026: digitar no campo de conclusão disparava `setConclusao` → o pai
  //    re-renderizava → o `textarea` era remontado → **o foco se perdia depois do
  //    primeiro caractere**, e a tela parecia "não aceitar digitação".
  //
  //    Chamadas como função (`{acoesDoChamado(detalhe)}`), o JSX entra na árvore do
  //    PRÓPRIO pai: não há fronteira de componente, logo não há remontagem, e os
  //    campos mantêm o foco. ⚠️ Não transformar de volta em `<Componente />` sem
  //    mover o estado (`conclusao`, `triagem`, `correcao`, `reagendar`) para dentro
  //    dele — senão o bug volta exatamente igual.
  const selo = (status, chamado) => {
    const c = CORES_STATUS[status] || CORES_STATUS['Encerrado'];
    // ⚠️ O chip (DEM-00027) tem rótulos PRÓPRIOS de status ('Aguardando Fernanda'
    //    = Atribuido, 'Concluída' = Encerrado): para a loja o pedido não é um
    //    chamado técnico. Fora do chip, o rótulo é o da esteira.
    const rotulo = (chamado && ehChip(chamado.TIPO_SOLICITACAO) && ROTULO_STATUS_CHIP[status])
      || ROTULO_STATUS[status] || status;
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 999,
        backgroundColor: c.fundo, color: c.texto, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
      }}>
        {rotulo}
      </span>
    );
  };

  /**
   * As AÇÕES do chip (DEM-00027) — fora da esteira técnica.
   *
   * ⚠️ Só duas: CONCLUIR (a Fernanda, ou o responsável atribuído) e CANCELAR (a
   *    Fernanda, ou o Gerente titular enquanto "Aguardando Fernanda"). Nada de
   *    triagem, assumir, encerrar-com-foto ou validação. Só há o que fazer
   *    enquanto o chip está em 'Atribuido'; depois disso (Concluída/Cancelada)
   *    não há ação.
   */
  const acoesDoChip = (c) => {
    if (c.STATUS !== 'Atribuido') return null;
    const podeConcluir = temAcessoTotal || Number(c.ID_TECNICO) === Number(user?.id);
    // ⚠️ O Gerente titular cancela enquanto a Fernanda não mexeu (espelho da
    //    exceção da rota). O recorte por loja já garante que é a loja dele.
    const podeCancel = temAcessoTotal || perfil === 'Gerente';
    if (!podeConcluir && !podeCancel) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
        {podeConcluir && (
          <button
            type="button" disabled={ocupado}
            onClick={() => agir(`/${c.ID}/chip/concluir`, {}, `Solicitação de chip #${c.ID} concluída.`)}
            style={{ ...btnAcao(COR), opacity: ocupado ? 0.5 : 1 }}
          >
            <CheckCircle2 size={14} /> Concluir solicitação
          </button>
        )}
        {podeCancel && (
          <div style={{
            width: '100%', padding: cancelamento.aberto ? 16 : '10px 16px',
            backgroundColor: '#fafafa', borderRadius: 8, border: '1px solid #fde68a',
          }}>
            <button
              type="button"
              onClick={() => setCancelamento((x) => ({ ...x, aberto: !x.aberto }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, background: 'none',
                border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: '#b45309', fontWeight: 700,
              }}
            >
              <XCircle size={14} color="#b45309" /> {cancelamento.aberto ? 'Deixar como está' : 'Cancelar solicitação'}
            </button>
            {cancelamento.aberto && (
              <div style={{ marginTop: 12 }}>
                <textarea
                  style={{ ...inputStyle, width: '100%', minHeight: 60, resize: 'vertical' }}
                  placeholder="Diga por que a solicitação está sendo cancelada."
                  value={cancelamento.motivo}
                  onChange={(e) => setCancelamento((x) => ({ ...x, motivo: e.target.value }))}
                />
                <button
                  type="button"
                  disabled={ocupado || !cancelamento.motivo.trim()}
                  style={{
                    ...btnAcao('#b45309'), marginTop: 10,
                    opacity: ocupado || !cancelamento.motivo.trim() ? 0.5 : 1,
                  }}
                  onClick={() => agir(
                    `/${c.ID}/cancelar`,
                    { motivo: cancelamento.motivo.trim(), fotos: [] },
                    `Solicitação de chip #${c.ID} cancelada.`,
                  )}
                >
                  <XCircle size={14} /> Confirmar o cancelamento
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /** As ações disponíveis para ESTE chamado e ESTE usuário. */
  const acoesDoChamado = (c) => {
    // ⚠️⚠️ O CHIP (DEM-00027) tem ações PRÓPRIAS, fora da esteira técnica — nada
    //    de triagem, assumir, encerrar-com-foto ou validação da loja.
    if (ehChip(c.TIPO_SOLICITACAO)) return acoesDoChip(c);
    // ⚠️⚠️ AQUI EXISTIA `const meu = Number(c.ID_TECNICO) === Number(user?.id)`,
    //    que escondia os botões de execução do chamado despachado para OUTRO
    //    técnico. Removido em 2026-08-28 (Julia) junto com a trava equivalente do
    //    backend: quem está na loja precisa poder assumir o chamado do colega sem
    //    depender de uma nova triagem.
    //    ⚠️ E desde a DEM-00009 (21/09) ASSUMIR/RETOMAR troca o dono: ao levar o
    //    chamado para 'Em atendimento', quem age vira o `ID_TECNICO` (com evento
    //    `Reatribuido`). Antes o dono só mudava na triagem, e retomar um Finalizado
    //    por outro técnico deixava a fila cobrando o técnico antigo. O backend faz
    //    isso (`assumeAoEntrarEmAtendimento`); a coluna *Aguardando* passa a nomear
    //    quem está no chamado agora, sem mudança de código aqui.
    // Sem data → nasce aberto (há o que fazer). Com data → nasce recolhido. O
    // clique da pessoa (`agendaManual`) sempre vence o padrão.
    const agendaAberta = agendaManual === null ? !c.DATA_AGENDADA : agendaManual;
    // ⚠️ O cálculo de "dias desde a conclusão" saiu daqui em 21/08/2026: ele
    //    servia só ao encerramento por prazo, que foi removido junto com a
    //    validação da loja.

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>

        {/* CORREÇÃO — acerta chamado aberto errado (pedido da Julia, 21/08).
            ⚠️ Vem PRIMEIRO nas ações de propósito: se o chamado está errado, o
            resto (triar, atender, encerrar) trabalha sobre informação errada.
            ⚠️ DOIS DONOS desde a DEM-00009 (21/09): o TI corrige a qualquer tempo
            (enquanto o trabalho não acabou); a LOJA corrige o que abriu só ANTES
            da triagem ('Aberto'). Depois disso, quem se arrependeu cancela e abre
            outro — o caminho existe e deixa rastro. O gate é `podeCorrigirChamado`
            (papel + estado), espelho da regra da rota. */}
        {/* ⚠️ RECOLHIDO POR PADRÃO (decisão da Julia, 2026-08-24): corrigir é
            EXCEÇÃO, não etapa. Aberto por padrão, ele empurrava para baixo o que
            se faz sempre (triar, atender, encerrar) e sugeria que corrigir fizesse
            parte do rito. Fechado, continua a um clique — e quem não precisa nem vê. */}
        {podeCorrigirChamado(c.STATUS) && (
          <div style={{ width: '100%', padding: corrigirAberto ? 16 : '10px 16px', backgroundColor: '#fafafa', borderRadius: 8, border: `1px solid ${COR_CLARA}` }}>
            <button
              type="button"
              onClick={() => setCorrigirAberto((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <Pencil size={14} color={COR} />
              <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                Corrigir o chamado
              </p>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>
                — só se o que a loja escreveu estiver errado
              </span>
              <span style={{ marginLeft: 'auto', color: '#94a3b8', display: 'flex' }}>
                {corrigirAberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
            </button>

            {corrigirAberto && (<>
            <p style={{ margin: '10px 0 12px 0', fontSize: 12, color: '#64748b' }}>
              Em branco = mantém o que a loja escreveu. Só muda o que você preencher.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Tipo</label>
                <select style={{ ...inputStyle, width: '100%' }} value={correcao.tipo_solicitacao}
                  onChange={(e) => setCorrecao((x) => ({ ...x, tipo_solicitacao: e.target.value }))}>
                  <option value="">Manter “{c.TIPO_SOLICITACAO || '—'}”</option>
                  {/* ⚠️⚠️ A CORREÇÃO NÃO ATRAVESSA A FRONTEIRA DO PEDIDO DE IMAGEM,
                      nos dois sentidos, e o servidor devolve 409 se tentar. Aqui a
                      opção nem é oferecida: entrar deixaria o chamado sendo
                      "Solicitação de Imagem" sem canais nem período, e sair
                      deixaria o pedido de gravação órfão. O caminho é cancelar e
                      abrir de novo. */}
                  {tipos
                    .filter((t) => t !== c.TIPO_SOLICITACAO)
                    .filter((t) => !ehSolicitacaoImagem(t) && !ehSolicitacaoImagem(c.TIPO_SOLICITACAO))
                    .map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Equipamento</label>
                <select style={{ ...inputStyle, width: '100%' }} value={correcao.id_ativo}
                  onChange={(e) => setCorrecao((x) => ({ ...x, id_ativo: e.target.value }))}>
                  <option value="">Manter “{c.EQUIPAMENTO_DESCRICAO || 'sem equipamento'}”</option>
                  {/* ⚠️ Só os ativos DESTA loja — o backend recusa o de outra, e
                      oferecer na tela o que o servidor recusa é armadilha. */}
                  {ativos.filter((a) => a.LOJA === c.LOJA).map((a) => (
                    <option key={a.CODIGO} value={a.CODIGO}>{a.FAMILIA} — {a.DESCRICAO}</option>
                  ))}
                  <option value="__limpar__">— tirar o equipamento —</option>
                </select>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Assunto</label>
                <input style={{ ...inputStyle, width: '100%' }} value={correcao.titulo} maxLength={150}
                  placeholder={c.TITULO}
                  onChange={(e) => setCorrecao((x) => ({ ...x, titulo: e.target.value }))} />
              </div>
            </div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
              O que está acontecendo
            </label>
            <textarea style={{ ...inputStyle, width: '100%', minHeight: 70, resize: 'vertical' }}
              placeholder={c.DESCRICAO} value={correcao.descricao}
              onChange={(e) => setCorrecao((x) => ({ ...x, descricao: e.target.value }))} />

            {/* FOTO da correção — OPCIONAL (DEM-00009). ⚠️ O teto conta as fotos
                que o chamado JÁ tem (`c.fotos`, e aqui `c` é o `detalhe`), porque
                MAX_FOTOS é do chamado, não deste envio — o mesmo que o backend
                reconfere. Dois gatilhos (câmera + galeria) e várias de uma vez,
                como os demais seletores do módulo. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 12 }}>
              {correcao.fotos.map((f) => (
                <span key={f.url} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  backgroundColor: COR_CLARA, borderRadius: 6, fontSize: 12, maxWidth: 200,
                }}>
                  <ImageIcon size={13} color={COR} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nome}</span>
                </span>
              ))}
              {(c.fotos?.length || 0) + correcao.fotos.length < MAX_FOTOS && (
                <EscolherAnexo
                  accept="image/*"
                  multiplo
                  cor={COR}
                  ocupado={ocupado}
                  rotulo="Foto (opcional)"
                  onArquivo={(a) => subirFotoCorrecao(a, c.LOJA)}
                  onArquivos={(arqs) => anexarFotosLote(arqs, {
                    momento: 'Correcao', loja: c.LOJA,
                    // ⚠️ O teto conta as que o chamado JÁ tem (`c.fotos`, e `c` é o
                    //    detalhe) MAIS as já preparadas nesta correção.
                    restante: MAX_FOTOS - ((c.fotos?.length || 0) + correcao.fotos.length),
                    append: (f) => setCorrecao((x) => ({ ...x, fotos: [...x.fotos, f] })),
                  })}
                />
              )}
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
              Anexo opcional. Este chamado tem <strong>{c.fotos?.length || 0}</strong> de {MAX_FOTOS} fotos.
            </p>

            <button
              type="button" disabled={ocupado || !algoACorrigir()}
              onClick={() => agir(`/${c.ID}`, corpoDaCorrecao(), `Chamado #${c.ID} corrigido.`)}
              style={{
                ...btnAcao(COR), marginTop: 10, padding: '9px 16px',
                backgroundColor: algoACorrigir() ? COR : '#e2e8f0',
                color: algoACorrigir() ? '#fff' : '#94a3b8',
                borderColor: algoACorrigir() ? COR : '#e2e8f0',
              }}
            >
              <Pencil size={14} /> Salvar correção
            </button>
            </>)}
          </div>
        )}

        {/* ATO 2 — triagem */}
        {podeTriar && c.STATUS === 'Aberto' && (
          <div style={{ width: '100%', padding: 16, backgroundColor: '#fafafa', borderRadius: 8, border: `1px solid ${COR_CLARA}` }}>
            <p style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Triagem</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Prioridade</label>
                <select
                  style={inputStyle} value={triagem.prioridade}
                  onChange={(e) => setTriagem((t) => ({ ...t, prioridade: e.target.value }))}
                >
                  {PRIORIDADES.map((p) => <option key={p} value={p}>{p === 'Media' ? 'Média' : p}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Técnico</label>
                <select
                  style={{ ...inputStyle, minWidth: 240 }} value={triagem.id_tecnico}
                  onChange={(e) => setTriagem((t) => ({ ...t, id_tecnico: e.target.value }))}
                >
                  <option value="">Escolha o técnico…</option>
                  {tecnicos.map((t) => <option key={t.ID} value={t.ID}>{t.NOME}</option>)}
                </select>
              </div>
              {/* Correção do tipo — opcional. ⚠️ Em branco = mantém o que a loja
                  escolheu (o backend usa COALESCE); só serve para consertar o
                  tipo errado, que é o que faz o relatório por tipo nascer torto. */}
              {/* A data da visita — OPCIONAL na triagem. Sem ela o chamado cai no
                  bloco "a agendar" do calendário, que é onde a cobrança acontece;
                  exigi-la aqui travaria o despacho de urgência ("vai agora"). */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
                  Ir à loja em — opcional
                </label>
                <input
                  type="date" style={inputStyle} value={triagem.data_agendada}
                  onChange={(e) => setTriagem((t) => ({ ...t, data_agendada: e.target.value }))}
                />
              </div>
              {/* ⚠️ O "corrigir o tipo" SAIU daqui em 2026-08-24 (decisão da Julia):
                  ele já existe no bloco "Corrigir o chamado", e o mesmo campo em
                  dois lugares da mesma tela é convite a divergência — a pessoa
                  corrige num, não vê o outro, e duvida de qual valeu.
                  ⚠️ O backend SEGUE aceitando `tipo_solicitacao` na triagem (a rota
                  não mudou); é a tela que deixou de oferecer o caminho duplicado. */}
              <button
                type="button" disabled={ocupado || !triagem.id_tecnico}
                onClick={() => agir(`/${c.ID}/triagem`, triagem, `Chamado #${c.ID} atribuído.`)}
                style={{
                  ...btnAcao(COR), backgroundColor: triagem.id_tecnico ? COR : '#e2e8f0',
                  color: triagem.id_tecnico ? '#fff' : '#94a3b8',
                  borderColor: triagem.id_tecnico ? COR : '#e2e8f0',
                  padding: '9px 16px', cursor: triagem.id_tecnico ? 'pointer' : 'not-allowed',
                }}
              >
                <UserCheck size={14} /> Atribuir
              </button>
            </div>
            {/* Os dois no mesmo ato: é isso que evita a fila sem responsável. */}
            <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
              A prioridade e o técnico são definidos juntos — é o que faz o chamado sair da fila de espera.
            </p>
          </div>
        )}

        {/* TROCAR O TÉCNICO ATRIBUÍDO (Julia, 2026-09-01) */}
        {/* ⚠️⚠️ SÓ EM 'Atribuido' — *"caso o chamado ainda não tenha sido
            executado"*. Quem manda é o `podeTrocarTecnico` do motor, do lado do
            servidor; esta condição é o ESPELHO dela na tela. Alargar aqui sem
            alargar lá dá o pior sintoma do módulo: botão que devolve 409.
            ⚠️ Em 'Aberto' o bloco não aparece porque ainda não há técnico —
            quem atribui é a triagem, no mesmo ato da prioridade. */}
        {podeTriar && c.STATUS === 'Atribuido' && (
          <div style={{ width: '100%', padding: trocaAberta ? 16 : '10px 16px', backgroundColor: '#fafafa', borderRadius: 8, border: `1px solid ${COR_CLARA}` }}>
            <button
              type="button"
              onClick={() => setTrocaAberta(!trocaAberta)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <UserCheck size={14} color={COR} />
              <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Técnico atribuído</p>
              <span style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>— {c.TECNICO_NOME || '—'}</span>
              <span style={{ marginLeft: 'auto', color: '#94a3b8', display: 'flex' }}>
                {trocaAberta ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
            </button>

            {trocaAberta && (<>
            <p style={{ margin: '10px 0 12px 0', fontSize: 12, color: '#64748b' }}>
              Trocar só vale <strong>antes de o atendimento começar</strong>. A prioridade e a data
              da visita não mudam — e o técnico novo recebe o aviso por e-mail.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <select
                style={{ ...inputStyle, minWidth: 240 }} value={trocaTecnico}
                onChange={(e) => setTrocaTecnico(e.target.value)}
              >
                <option value="">Escolha o novo técnico…</option>
                {/* ⚠️ Tira da lista quem JÁ é o técnico: o servidor devolve 400
                    nesse caso (troca para o mesmo não gera evento), e oferecer a
                    opção seria convidar ao erro. */}
                {tecnicos
                  .filter((t) => Number(t.ID) !== Number(c.ID_TECNICO))
                  .map((t) => <option key={t.ID} value={t.ID}>{t.NOME}</option>)}
              </select>
              <button
                type="button" disabled={ocupado || !trocaTecnico}
                onClick={() => agir(`/${c.ID}/tecnico`, { id_tecnico: trocaTecnico }, `Chamado #${c.ID} passou para outro técnico.`)}
                style={{
                  ...btnAcao(COR),
                  backgroundColor: trocaTecnico ? COR : '#e2e8f0',
                  color: trocaTecnico ? '#fff' : '#94a3b8',
                  borderColor: trocaTecnico ? COR : '#e2e8f0',
                  padding: '9px 16px', cursor: trocaTecnico ? 'pointer' : 'not-allowed',
                }}
              >
                <UserCheck size={14} /> Trocar técnico
              </button>
            </div>
            </>)}
          </div>
        )}

        {/* (Re)agendamento — separado da triagem porque remarcar é o caso comum
            (a loja pede, o técnico ficou preso em outra loja, a peça não chegou),
            e refazer a triagem reescreveria a data em que ela aconteceu. */}
        {/* ⚠️ RECOLHIDO QUANDO JÁ TEM DATA (decisão da Julia, 2026-08-24): visita
            marcada é assunto resolvido, e um formulário aberto para remarcar sugere
            que remarcar seja o passo seguinte. SEM data ele nasce ABERTO, porque aí
            sim há algo a fazer — a mesma tela, dois estados, conforme o que falta. */}
        {podeTriar && !trabalhoAcabou(c.STATUS) && c.ID_TECNICO && (
          <div style={{ width: '100%', padding: agendaAberta ? 16 : '10px 16px', backgroundColor: '#fafafa', borderRadius: 8, border: `1px solid ${COR_CLARA}` }}>
            <button
              type="button"
              /* ⚠️ Inverte o estado EFETIVO, não o guardado: `agendaManual` começa
                 `null` e `!null` é `true` — com o bloco já aberto (chamado sem
                 data), o primeiro clique não fecharia nada. */
              onClick={() => setAgendaManual(!agendaAberta)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <CalendarDays size={14} color={COR} />
              <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Agenda da visita</p>
              <span style={{ fontSize: 12, color: c.DATA_AGENDADA ? '#334155' : '#b45309', fontWeight: c.DATA_AGENDADA ? 700 : 400 }}>
                {c.DATA_AGENDADA ? `— ${fmtData(c.DATA_AGENDADA)}` : '— sem data'}
              </span>
              <span style={{ marginLeft: 'auto', color: '#94a3b8', display: 'flex' }}>
                {agendaAberta ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
            </button>

            {agendaAberta && (<>
            <p style={{ margin: '10px 0 12px 0', fontSize: 12, color: '#64748b' }}>
              {c.DATA_AGENDADA
                ? <>Marcada para <strong>{fmtData(c.DATA_AGENDADA)}</strong>. A data é opcional — desmarcar não cancela nada.</>
                : <>Sem data — aparece em <strong>“a agendar”</strong> no calendário da Visão Geral. Agendar é opcional.</>}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <input type="date" style={inputStyle} value={reagendar} onChange={(e) => setReagendar(e.target.value)} />
              <button
                type="button" disabled={ocupado || !reagendar} style={{
                  ...btnAcao(COR),
                  backgroundColor: reagendar ? COR : '#e2e8f0',
                  color: reagendar ? '#fff' : '#94a3b8',
                  borderColor: reagendar ? COR : '#e2e8f0',
                  padding: '9px 16px',
                }}
                onClick={() => agir(`/${c.ID}/agendar`, { data_agendada: reagendar }, `Chamado #${c.ID} agendado.`)}
              >
                <CalendarDays size={14} /> {c.DATA_AGENDADA ? 'Remarcar' : 'Agendar'}
              </button>
              {/* Desmarcar ≠ cancelar: o trabalho continua a fazer, só perdeu a data. */}
              {c.DATA_AGENDADA && (
                <button type="button" disabled={ocupado} style={btnAcao('#94a3b8')}
                  onClick={() => agir(`/${c.ID}/agendar`, { data_agendada: null }, `Chamado #${c.ID} voltou para "a agendar".`)}>
                  Desmarcar
                </button>
              )}
            </div>
            </>)}
          </div>
        )}

        {/* ATO 3 — o técnico */}
        {/* ⚠️ O RÓTULO É "EXECUTAR CHAMADO" desde 2026-08-28 (Julia). Era
            "Assumir e começar", que descrevia o ato pelo lado de quem estava
            sendo escalado — e deixou de caber quando qualquer um da equipe
            passou a poder pegar qualquer chamado triado.
            ⚠️ O status gravado NÃO mudou: continua 'Em atendimento'. Trocou a
            palavra na tela, não a máquina de estados. */}
        {podeExecutar && c.STATUS === 'Atribuido' && (
          <button type="button" disabled={ocupado} style={btnAcao('#3730a3')}
            onClick={() => agir(`/${c.ID}/status`, { status: 'Em atendimento' }, `Você assumiu o chamado #${c.ID}.`)}>
            <PlayCircle size={14} /> Executar chamado
          </button>
        )}
        {/* ⚠️ "Pausar atendimento" e "Retomar" FICAVAM AQUI, soltos acima do
            formulário de encerramento. Desceram em 2026-08-27 (Julia) para a
            linha de ações do fim do bloco, ao lado do "Encerrar" — ver o
            comentário lá embaixo. */}

        {/* ⚠️ DEM-00009 (frente B): o gerente titular encerra o que a Sonda resolveu,
            mas SÓ de 'Em atendimento' (não da pausa). O encerramento dele vai DIRETO
            a Validado (o backend encadeia) — a mensagem de sucesso abaixo diz isso. */}
        {((podeExecutar && (c.STATUS === 'Em atendimento' || estaPausado(c.STATUS)))
          || (podeFecharSonda && c.STATUS === 'Em atendimento')) && (
          <div style={{ width: '100%', padding: 16, backgroundColor: '#fafafa', borderRadius: 8, border: `1px solid ${COR_CLARA}` }}>
            <p style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Encerrar o chamado</p>
            <textarea
              style={{ ...inputStyle, width: '100%', minHeight: 90, resize: 'vertical' }}
              placeholder="O que foi feito para resolver."
              value={conclusao.observacao}
              onChange={(e) => setConclusao((x) => ({ ...x, observacao: e.target.value }))}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 12 }}>
              {conclusao.fotos.map((f) => (
                <span key={f.url} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  backgroundColor: COR_CLARA, borderRadius: 6, fontSize: 12, maxWidth: 200,
                }}>
                  <ImageIcon size={13} color={COR} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nome}</span>
                </span>
              ))}
              {/* Dois gatilhos (câmera + galeria) — a câmera de volta no Android, e
                  a galeria aceita várias de uma vez, com o teto no `anexarFotosLote`. */}
              {conclusao.fotos.length < MAX_FOTOS && (
                <EscolherAnexo
                  accept="image/*"
                  multiplo
                  cor={COR}
                  ocupado={ocupado}
                  rotulo={conclusao.fotos.length < MIN_FOTOS_ENCERRAMENTO
                    ? `Foto do serviço (faltam ${MIN_FOTOS_ENCERRAMENTO - conclusao.fotos.length})`
                    : 'Escolher da galeria'}
                  onArquivo={(a) => subirFotoConclusao(a, c.LOJA)}
                  onArquivos={(arqs) => anexarFotosLote(arqs, {
                    momento: 'Conclusao', loja: c.LOJA,
                    restante: MAX_FOTOS - conclusao.fotos.length,
                    append: (f) => setConclusao((x) => ({ ...x, fotos: [...x.fotos, f] })),
                  })}
                />
              )}
            </div>
            {/* Dizer a exigência ANTES de o botão travar evita o "por que não
                salva?" — o botão desabilitado sem explicação é o pior retorno. */}
            <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
              A descrição e <strong>ao menos {MIN_FOTOS_ENCERRAMENTO} fotos</strong> do serviço são
              obrigatórias — elas são a única evidência de que o serviço aconteceu.
              Anexadas: <strong>{conclusao.fotos.length}</strong> de {MAX_FOTOS}.
            </p>

            {/* ==============================================================
                A CIÊNCIA DO GERENTE SAIU DAQUI EM 31/08/2026 (Julia).

                ⚠️⚠️ Ela era um seletor OBRIGATÓRIO neste formulário: o técnico
                   escolhia um gerente numa lista, no aparelho dele, e sem isso o
                   chamado não encerrava. Virou ATO PRÓPRIO — o gerente entra no
                   portal, abre o chamado já encerrado e clica em *Validar*.

                ⚠️ Ganhou-se o que a tela antiga avisava não ter: agora quem
                   valida está AUTENTICADO, e `VALIDADO_POR` é quem de fato
                   validou. Perdeu-se a garantia de que a ciência acontece junto
                   com o serviço — por isso o chamado encerrado FICA NA FILA, com
                   "Aguardando validação" e o "parado há" correndo.

                ⚠️ O bloco de validação agora fica FORA deste formulário, mais
                   abaixo, e só aparece para quem pode validar.
                ============================================================== */}

            {/* ================================================================
                A LINHA DE AÇÕES DO TÉCNICO — "Encerrar" e "Pausar atendimento"
                LADO A LADO (Julia, 2026-08-27).

                ⚠️ O "Pausar"/"Retomar" DESCEU até aqui; o "Encerrar" não subiu.
                   Os dois ficam juntos, que era o pedido, sem que o botão que
                   ENVIA passe a ficar acima dos campos que ele envia — um
                   "Encerrar" no topo, desabilitado até o formulário abaixo estar
                   preenchido, é o retorno que faz a pessoa clicar e achar que a
                   tela travou.

                ⚠️ São as duas saídas do MESMO momento: o técnico terminou (encerra)
                   ou não vai terminar agora (pausa). Ficarem separados por um
                   formulário inteiro escondia a segunda — e chamado que fica "em
                   atendimento" sem ninguém atendendo é o número que a fila existe
                   para mostrar.
                ⚠️ A ORDEM é deliberada: encerrar é o caminho comum e vem primeiro,
                   em verde sólido; pausar é a exceção e fica ao lado, discreto.
                ================================================================ */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 14,
            }}>
              <button
                type="button"
                disabled={ocupado || !podeEncerrar()}
                onClick={() => agir(`/${c.ID}/encerrar`, {
                  observacao: conclusao.observacao.trim(),
                  fotos: conclusao.fotos.map((f) => f.url),
                  // ⚠️ O `validado_por` SAIU daqui em 31/08: a ciência virou ato
                  //    próprio da loja, DEPOIS do encerramento.
                  // ⚠️ Mensagem conforme quem encerra: o gerente titular fecha o que
                  //    a Sonda resolveu e o chamado JÁ vai validado (frente B); o
                  //    técnico encerra e a loja ainda precisa dar ciência.
                }, podeFecharSonda
                  ? `Chamado #${c.ID} encerrado e validado.`
                  : `Chamado #${c.ID} encerrado. Agora falta a loja validar o serviço.`)}
                style={{
                  ...btnAcao('#16a34a'),
                  backgroundColor: podeEncerrar() ? '#16a34a' : '#e2e8f0',
                  color: podeEncerrar() ? '#fff' : '#94a3b8',
                  borderColor: podeEncerrar() ? '#16a34a' : '#e2e8f0',
                  padding: '9px 16px',
                }}
              >
                {/* ⚠️ O rótulo nomeia OS DOIS atos, porque o botão faz os dois:
                    encerra o serviço e registra a ciência, com data e hora. Só
                    "Encerrar" esconderia metade do que o clique grava. */}
                {/* ⚠️ O rótulo voltou a ser só "Encerrar" em 31/08: o clique deixou
                    de registrar a ciência, que virou ato da loja. Manter "Validar e
                    encerrar" prometeria o que ele não faz mais. */}
                <CheckCircle2 size={14} /> Encerrar chamado
              </button>

              {/* ⚠️⚠️ ESTE BOTÃO NÃO PAUSA — ele ABRE O BLOCO logo abaixo, onde o
                  motivo é digitado (Julia, 17/09/2026). Pausar em um clique era o
                  que permitia parar o relógio do SLA sem dizer por quê.
                  ⚠️ O estado gravado é 'Atendimento Pausado'. Ele se chamava
                  'Aguardando loja' até 17/09 — nome que descrevia UM dos motivos de
                  parada (a loja) e mentia nos outros: o técnico pausa também por
                  falta de acesso, movimento na loja ou espera de terceiro. O rótulo
                  do botão já dizia "Pausar" desde 27/08; agora o banco diz o mesmo.
                  ⚠️ Clicar de novo FECHA o bloco sem apagar o que foi digitado. */}
              {c.STATUS === 'Em atendimento' && (
                <button type="button" disabled={ocupado}
                  style={btnAcao(pausa.aberto ? '#c2410c' : '#9a3412')}
                  onClick={() => setPausa((x) => ({ ...x, aberto: !x.aberto }))}>
                  <PauseCircle size={14} /> Pausar atendimento
                </button>
              )}
              {/* Retomar ocupa exatamente o lugar do Pausar — são o mesmo botão em
                  dois estados, e trocá-los de posição faria a dupla dançar.
                  ⚠️⚠️ SÓ A PAUSA, e não o `Finalizado`: este bloco inteiro é o
                  formulário de ENCERRAR, e ele não é renderizado num chamado
                  finalizado. A retomada da finalização tem painel próprio, logo
                  abaixo — ver o porquê lá. */}
              {estaPausado(c.STATUS) && (
                <button type="button" disabled={ocupado} style={btnAcao('#3730a3')}
                  onClick={() => agir(`/${c.ID}/status`, { status: 'Em atendimento' }, `Chamado #${c.ID} retomado.`)}>
                  <PlayCircle size={14} /> Retomar
                </button>
              )}

              {/* ==============================================================
                  AS TRÊS SAÍDAS DO ATENDIMENTO (31/08/2026; a 3ª em 01/09).

                  ⚠️ Ficam ao lado do *Pausar* porque são irmãs dele: as quatro são
                     "não vou terminar agora". O que as separa é DE QUEM se espera
                     — a loja (pausar), a Sonda (Invgate), uma peça a comprar, ou
                     o fornecedor que está consertando a peça.
                  ⚠️ São BOTÕES QUE ABREM UM BLOCO, não que agem: cada motivo pede um
                     dado, e finalizar sem ele deixaria o chamado parado sem
                     ninguém saber esperando o quê.
                  ⚠️ Clicar de novo FECHA o bloco — quem abriu por engano não fica
                     preso a um formulário que não quer preencher.
                  ⚠️ Trocar de motivo PRESERVA as linhas digitadas de propósito: os
                     dois motivos de peça compartilham o campo *produto*, e quem
                     clicou no botão errado não perde o que escreveu.
                  ============================================================== */}
              {c.STATUS === 'Em atendimento' && (
                <>
                  <button type="button" disabled={ocupado}
                    style={btnAcao(finalizacao.motivo === 'Invgate' ? '#0e7490' : '#78350f')}
                    onClick={() => setFinalizacao((f) => ({
                      ...f, motivo: f.motivo === 'Invgate' ? '' : 'Invgate',
                    }))}>
                    <ExternalLink size={14} /> Invgate
                  </button>
                  <button type="button" disabled={ocupado}
                    style={btnAcao(finalizacao.motivo === 'Pecas' ? '#0e7490' : '#78350f')}
                    onClick={() => setFinalizacao((f) => ({
                      ...f, motivo: f.motivo === 'Pecas' ? '' : 'Pecas',
                    }))}>
                    <ShoppingCart size={14} /> Peças a comprar
                  </button>
                  <button type="button" disabled={ocupado}
                    style={btnAcao(finalizacao.motivo === 'Reparo' ? '#0e7490' : '#78350f')}
                    onClick={() => setFinalizacao((f) => ({
                      ...f, motivo: f.motivo === 'Reparo' ? '' : 'Reparo',
                    }))}>
                    <Wrench size={14} /> Peças em reparo
                  </button>
                </>
              )}
            </div>

            {/* ================================================================
                O BLOCO DA PAUSA (Julia, 17/09/2026) — o motivo é obrigatório.

                ⚠️⚠️ PAUSAR DEIXOU DE SER UM CLIQUE SÓ, e o custo é deliberado: a
                   pausa PARA O RELÓGIO DO SLA, e até aqui dava para parar o prazo
                   indefinidamente sem registrar por quê. O campo é o que torna a
                   parada legítima — e é o texto que a loja e o gerente leem na
                   coluna *Aguardando* enquanto o chamado não anda.

                ⚠️ Fica ABAIXO da linha de ações, no lugar onde o bloco da
                   finalização também abre: os dois nascem do mesmo movimento
                   ("não vou terminar agora") e alternar entre eles não deve fazer
                   a tela pular.
                ⚠️ Clicar de novo no botão FECHA o bloco — e o que foi digitado
                   fica, porque quem fechou por engano não deve perder o texto.
                ================================================================ */}
            {c.STATUS === 'Em atendimento' && pausa.aberto && (
              <div style={{
                marginTop: 14, padding: 14, borderRadius: 8,
                backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 800, color: '#9a3412' }}>
                  Pausar o atendimento
                </p>
                <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#9a3412', lineHeight: 1.5 }}>
                  O chamado <strong>continua seu</strong> e o <strong>prazo do SLA fica
                  parado</strong> enquanto ele estiver pausado. Diga o que está travando —
                  é o que aparece na fila para a loja e para o gerente.
                </p>
                <label style={rotuloBloco} htmlFor={`ch-pausa-${c.ID}`}>
                  Motivo da pausa
                </label>
                <textarea
                  id={`ch-pausa-${c.ID}`}
                  style={{ ...inputStyle, width: '100%', minHeight: 70, resize: 'vertical' }}
                  placeholder="ex.: loja não liberou o balcão para a troca; volto às 15h"
                  value={pausa.motivo}
                  maxLength={500}
                  onChange={(e) => setPausa((x) => ({ ...x, motivo: e.target.value }))}
                />
                {/* Dizer a exigência ANTES de o botão travar: botão apagado sem
                    explicação é o pior retorno que a tela pode dar. */}
                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#c2410c' }}>
                  Ao menos {MIN_MOTIVO_PAUSA} caracteres.
                </p>
                <button
                  type="button"
                  disabled={ocupado || pausa.motivo.trim().length < MIN_MOTIVO_PAUSA}
                  onClick={() => agir(
                    `/${c.ID}/status`,
                    { status: 'Atendimento Pausado', motivo: pausa.motivo.trim() },
                    `Atendimento do chamado #${c.ID} pausado.`,
                  )}
                  style={{
                    ...btnAcao('#9a3412'),
                    marginTop: 12,
                    backgroundColor: pausa.motivo.trim().length >= MIN_MOTIVO_PAUSA ? '#9a3412' : '#e2e8f0',
                    color: pausa.motivo.trim().length >= MIN_MOTIVO_PAUSA ? '#fff' : '#94a3b8',
                    borderColor: pausa.motivo.trim().length >= MIN_MOTIVO_PAUSA ? '#9a3412' : '#e2e8f0',
                    padding: '9px 16px',
                    // ⚠️ ALVO DE TOQUE no aparelho: quem pausa é o técnico de pé na
                    //    loja, com o celular numa mão. Os 30px do botão padrão do
                    //    módulo são confortáveis no mouse e não no polegar — e este
                    //    aqui é o que GRAVA.
                    ...(ehMobile ? { minHeight: 44, width: '100%', justifyContent: 'center' } : {}),
                  }}
                >
                  <PauseCircle size={14} /> Confirmar a pausa
                </button>
              </div>
            )}

            {/* ================================================================
                O MOTIVO DA PAUSA VIGENTE.

                ⚠️ Aparece com o chamado JÁ pausado, e é metade do sentido do
                   campo: quem abre a ficha precisa ver o que trava o serviço sem
                   ter de descer até a linha do tempo. O `PAUSA_MOTIVO` guarda a
                   ÚLTIMA pausa — por isso só se mostra quando o status é de pausa.
                ⚠️ Sem motivo = chamado pausado ANTES de 17/09/2026, quando o campo
                   não existia. A frase diz isso em vez de mostrar um vazio.
                ================================================================ */}
            {estaPausado(c.STATUS) && (
              <div style={{
                marginTop: 14, padding: 12, borderRadius: 8,
                backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
              }}>
                <p style={{ margin: 0, fontSize: 12, color: '#9a3412', lineHeight: 1.5 }}>
                  <strong>Atendimento pausado</strong>
                  {c.PAUSA_MOTIVO
                    ? <> — {c.PAUSA_MOTIVO}</>
                    : <> — pausado antes de o motivo passar a ser registrado.</>}
                </p>
              </div>
            )}

            {/* O BLOCO DA FINALIZAÇÃO — só abre depois de escolher o motivo. */}
            {c.STATUS === 'Em atendimento' && finalizacao.motivo && (
              <div style={{
                marginTop: 14, padding: 14, borderRadius: 8,
                backgroundColor: '#fffbeb', border: '1px solid #fde68a',
              }}>
                {/* ⚠️ Mapa e não ternário — a mesma armadilha que o `aguardandoDe`
                    tinha no servidor: com um terceiro motivo, o `? :` rotularia
                    todo reparo como "Invgate". */}
                <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 800, color: '#78350f' }}>
                  Finalizar o atendimento — {ROTULO_FINALIZACAO[finalizacao.motivo] || 'terceiro'}
                </p>
                {/* ⚠️⚠️ DIZ QUE O CHAMADO NÃO ACABA AQUI. "Finalizar" é a palavra
                    mais perigosa desta tela: sem esta frase, o técnico entende que
                    está fechando o chamado — e a loja nunca recebe a validação. */}
                <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                  O chamado <strong>não é encerrado</strong> agora — ele fica esperando
                  {' '}{ESPERA_FINALIZACAO[finalizacao.motivo] || 'um terceiro'}.
                  Quando resolver, volte aqui e clique em <strong>Retomar e encerrar</strong>.
                  {/* ⚠️ Diz quais campos travam o botão. Sem isto, quem vê o botão
                      apagado procura o erro no que já preencheu. */}
                  {finalizacao.motivo === 'Pecas' && (
                    <> <strong>Produto e quantidade</strong> são obrigatórios; o
                    <strong> fornecedor e o valor podem ficar em branco</strong> — quem
                    normalmente preenche a cotação é o Gerente de TI, depois, pelo
                    painel de peças.</>
                  )}
                  {finalizacao.motivo === 'Reparo' && (
                    <> <strong>Produto e fornecedor</strong> são obrigatórios — é o
                    que diz onde a peça está. Aqui não há quantidade nem valor.</>
                  )}
                </p>

                {/* ==============================================================
                    A PREVISÃO DE CONCLUSÃO (SLA, 09/09) — obrigatória.

                    ⚠️⚠️ ELA FICA NO TOPO DO BLOCO, ANTES DO CONTEÚDO DO MOTIVO, e
                       é deliberado: é o campo que o técnico mais vai querer pular,
                       porque é o único que o compromete. Embaixo da lista de peças
                       ele seria descoberto depois de o botão já estar apagado.

                    ⚠️ É o que TORNA LEGÍTIMA a pausa do relógio: finalizar para o
                       SLA, e a regra da Julia é "registrar o motivo E a previsão".
                       Sem a data, "peça pendente" viraria desculpa sem prazo.
                    ============================================================== */}
                <div style={{ marginBottom: 14 }}>
                  <label style={rotuloBloco} htmlFor={`ch-previsao-${c.ID}`}>
                    Previsão de conclusão
                  </label>
                  <input
                    id={`ch-previsao-${c.ID}`}
                    type="date"
                    style={{ ...inputStyle, maxWidth: 200 }}
                    value={finalizacao.previsao_conclusao}
                    /* ⚠️ `min` = hoje, espelhando a trava do servidor. Ele recusa
                       data passada (`validarPrevisao`); sem o `min`, o calendário
                       oferece ontem e o técnico só descobre no 400. */
                    min={diaLocalIso(new Date())}
                    onChange={(e) => setFinalizacao((f) => ({ ...f, previsao_conclusao: e.target.value }))}
                  />
                  <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#92400e' }}>
                    Quando você espera que isto esteja resolvido. <strong>O prazo do
                    SLA fica pausado</strong> enquanto o chamado espera — e é esta
                    data que passa a ser cobrada.
                  </p>
                </div>

                {!ehMotivoDePeca(finalizacao.motivo) ? (
                  <>
                    <label style={rotuloBloco} htmlFor={`ch-invgate-${c.ID}`}>
                      Número do chamado no Invgate
                    </label>
                    <input
                      id={`ch-invgate-${c.ID}`}
                      style={{ ...inputStyle, width: '100%', maxWidth: 280 }}
                      placeholder="ex.: INC-4821"
                      value={finalizacao.invgate_chamado}
                      onChange={(e) => setFinalizacao((f) => ({ ...f, invgate_chamado: e.target.value }))}
                    />
                    <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#92400e' }}>
                      {/* ⚠️ Diz PARA QUE serve o número — é ele que liga o nosso
                          chamado ao deles quando alguém for cobrar. */}
                      É por ele que se cobra a Sonda depois. Sem o número, ninguém acha
                      lá o que foi aberto daqui.
                    </p>
                  </>
                ) : (
                  <>
                    {/* ⚠️ Uma linha por peça: um reparo costuma precisar de mais de
                        uma, e com um campo só o técnico escreveria "2 fontes + 1 cabo"
                        na descrição — o campo livre que o módulo existe para matar. */}
                    {finalizacao.pecas.map((peca, idx) => (
                      <div key={idx} style={{
                        display: 'grid', gap: 8, marginBottom: 10,
                        // ⚠️ O REPARO TEM DUAS COLUNAS, não quatro com duas apagadas:
                        //    campo desabilitado ainda se lê como "algo que falta
                        //    preencher". Aqui não falta — não existe.
                        gridTemplateColumns: ehMobile ? '1fr'
                          : (finalizacao.motivo === 'Reparo' ? '2fr 1.6fr auto' : '2fr 0.7fr 1fr 1.3fr auto'),
                        alignItems: 'end',
                      }}>
                        <div>
                          {idx === 0 && <label style={rotuloBloco}>Produto</label>}
                          <input style={{ ...inputStyle, width: '100%' }}
                            placeholder={finalizacao.motivo === 'Reparo' ? 'Bumpbar do KVS 2' : 'Fonte 12V do KVS'}
                            value={peca.descricao}
                            onChange={(e) => mudarPeca(idx, 'descricao', e.target.value)} />
                        </div>
                        {/* ⚠️ Quantidade e valor SÓ na compra. No reparo manda-se
                            AQUELA peça (quantidade seria sempre 1) e o preço do
                            conserto não se sabe no ato de despachar. */}
                        {finalizacao.motivo === 'Pecas' && (
                          <>
                            <div>
                              {idx === 0 && <label style={rotuloBloco}>Qtd.</label>}
                              <input style={{ ...inputStyle, width: '100%' }} inputMode="decimal"
                                value={peca.quantidade}
                                onChange={(e) => mudarPeca(idx, 'quantidade', e.target.value)} />
                            </div>
                            <div>
                              {idx === 0 && <label style={rotuloBloco}>Valor un. (R$)</label>}
                              {/* ⚠️ OPCIONAL, e o placeholder diz isso: o técnico
                                  finaliza na loja, muitas vezes antes de ter cotação.
                                  Exigir faria ele inventar um número — pior que campo
                                  vazio, porque ninguém sabe que é chute. */}
                              <input style={{ ...inputStyle, width: '100%' }} inputMode="decimal"
                                placeholder="opcional"
                                value={peca.valor_unitario}
                                onChange={(e) => mudarPeca(idx, 'valor_unitario', e.target.value)} />
                            </div>
                          </>
                        )}
                        <div>
                          {idx === 0 && (
                            <label style={rotuloBloco}>
                              {finalizacao.motivo === 'Reparo' ? 'Fornecedor (onde está)' : 'Fornecedor'}
                            </label>
                          )}
                          {/* ⚠️⚠️ O PLACEHOLDER É A METADE VISÍVEL DA REGRA, e ele já
                              mudou duas vezes: nasceu "opcional", virou obrigatório em
                              31/08 e voltou a ser opcional NA COMPRA em 01/09 (quem
                              cota é o Gerente de TI, depois). No REPARO ele é
                              obrigatório — é o campo que diz onde a peça está. Deixar
                              o texto errado faz o botão travar sem o campo dizer por
                              quê, que foi exatamente o problema de 31/08. */}
                          <input style={{ ...inputStyle, width: '100%' }}
                            placeholder={finalizacao.motivo === 'Reparo' ? 'para onde foi' : 'opcional — pode ficar para depois'}
                            value={peca.fornecedor}
                            onChange={(e) => mudarPeca(idx, 'fornecedor', e.target.value)} />
                        </div>
                        {/* ⚠️ Nunca some a ÚLTIMA linha: uma lista sem nenhuma entrada
                            obrigaria dois cliques para digitar a primeira peça, que é
                            o caso de todas as vezes. */}
                        <button type="button" disabled={finalizacao.pecas.length === 1}
                          onClick={() => removerPeca(idx)} title="Remover esta peça"
                          style={{
                            ...btnAcao('#94a3b8'), padding: '9px 10px',
                            opacity: finalizacao.pecas.length === 1 ? 0.4 : 1,
                          }}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={acrescentarPeca} style={btnAcao('#78350f')}>
                      + Acrescentar peça
                    </button>
                    {totalDoOrcamento() != null && (
                      <p style={{ margin: '10px 0 0 0', fontSize: 13, color: '#78350f', fontWeight: 700 }}>
                        {/* ⚠️ O aviso de PARCIAL é obrigatório: um total que ignora as
                            peças sem cotação, mostrado como se fosse o orçamento
                            inteiro, subestima a compra. */}
                        Orçamento: {fmtMoeda(totalDoOrcamento())}
                        {finalizacao.pecas.some((x) => x.descricao.trim() && !String(x.valor_unitario).trim()) && (
                          <span style={{ fontWeight: 500, color: '#92400e' }}> — parcial, há peça sem valor</span>
                        )}
                      </p>
                    )}
                  </>
                )}

                {/* ⚠️ O aviso de rascunho é discreto — ele tranquiliza quem digitou
                    sete linhas de peça e vai fechar o modal. "Erro" é o único que
                    salta: é ele que avisa que fechar agora custa o preenchimento.
                    ⚠️ Diz explicitamente que o chamado NÃO foi finalizado — sem
                    isso, "salvo" na mesma tela do botão *Finalizar* se lê como
                    "pronto, já foi". */}
                {autosaveFinal !== 'ocioso' && (
                  <p style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    margin: '10px 0 0 0', fontSize: 12,
                    color: autosaveFinal === 'erro' ? '#b91c1c' : '#92400e',
                    fontWeight: autosaveFinal === 'erro' ? 600 : 400,
                  }}>
                    <FileClock size={13} />
                    {autosaveFinal === 'salvando' && 'Salvando rascunho…'}
                    {autosaveFinal === 'salvo' && 'Rascunho salvo — o chamado ainda NÃO foi finalizado.'}
                    {autosaveFinal === 'erro' && 'Não conseguimos salvar o rascunho. Se fechar agora, perde o preenchimento.'}
                  </p>
                )}

                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    disabled={ocupado || !podeFinalizar()}
                    onClick={() => agir(`/${c.ID}/finalizar`, corpoDaFinalizacao(),
                      `Atendimento do chamado #${c.ID} finalizado.`)}
                    style={{
                      ...btnAcao('#b45309'), padding: '9px 16px',
                      backgroundColor: podeFinalizar() ? '#b45309' : '#e2e8f0',
                      color: podeFinalizar() ? '#fff' : '#94a3b8',
                      borderColor: podeFinalizar() ? '#b45309' : '#e2e8f0',
                    }}
                  >
                    <PauseCircle size={14} /> Finalizar atendimento
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================================================================
            O CHAMADO ESTÁ ESPERANDO UM TERCEIRO — e é daqui que ele volta.

            ⚠️⚠️ ESTE BLOCO É O CONSERTO DE UM BECO SEM SAÍDA que eu mesmo criei em
               31/08/2026: o botão *Retomar* tinha nascido dentro da linha de ações
               do formulário de ENCERRAR, que só é renderizado em 'Em atendimento'
               e na pausa. Num chamado `Finalizado` o formulário inteiro
               não aparece — então o botão existia no código e era INALCANÇÁVEL na
               tela, e o chamado ficava preso esperando para sempre. A pergunta que
               o achou foi a mais direta possível: "como faço para retomar, se as
               peças chegaram?".

            ⚠️ Painel PRÓPRIO e não um remendo no gate do encerramento: aqui o
               técnico não vai encerrar (o motor não permite `Finalizado →
               Encerrado`, decisão da Julia — retomar volta para 'Em atendimento'),
               então oferecer o formulário de encerrar seria oferecer um botão que
               devolve 409.

            ⚠️ Ele DIZ O QUE ESTÁ ESPERANDO, com o número do Invgate ou a contagem
               de peças: é a informação que decide se já dá para retomar.
            ================================================================== */}
        {/* ⚠️ DEM-00009 (frente B): o gerente titular também retoma o que a Sonda
            resolveu — `podeFecharSonda`. O técnico segue vendo por `podeExecutar`. */}
        {(podeExecutar || podeFecharSonda) && c.STATUS === 'Finalizado' && (
          <div style={{
            width: '100%', padding: 16, borderRadius: 8,
            backgroundColor: '#fffbeb', border: '1px solid #fde68a',
          }}>
            <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 800, color: '#78350f' }}>
              Atendimento finalizado — aguardando {c.FINALIZADO_MOTIVO === 'Pecas' ? 'a peça' : 'o Invgate'}
            </p>
            <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
              {c.FINALIZADO_MOTIVO === 'Pecas'
                ? <>As peças foram lançadas e o chamado espera a compra chegar. <strong>Quando
                  chegarem</strong>, retome o atendimento aqui — o chamado volta para
                  <em> em atendimento</em> e aí você registra o serviço e encerra.</>
                : <>O chamado foi escalado para a Sonda{c.INVGATE_CHAMADO
                  ? <> (Invgate <strong>{c.INVGATE_CHAMADO}</strong>)</> : null}.
                  <strong> Quando eles resolverem</strong>, retome o atendimento aqui — o chamado
                  volta para <em>em atendimento</em> e aí você registra o serviço e encerra.</>}
            </p>
            {/* ⚠️ O rótulo diz PARA ONDE leva, não só o verbo: "Retomar" sozinho não
                deixa claro que o próximo passo é fechar o chamado. */}
            <button type="button" disabled={ocupado} style={{ ...btnAcao('#3730a3'), padding: '9px 16px' }}
              onClick={() => agir(`/${c.ID}/status`, { status: 'Em atendimento' },
                `Chamado #${c.ID} retomado — registre o serviço e encerre.`)}>
              <PlayCircle size={14} /> Retomar atendimento
            </button>
          </div>
        )}

        {/* ⚠️⚠️ AQUI FICAVAM DOIS BLOCOS, REMOVIDOS EM 21/08/2026 (decisão da
            Julia): a VALIDAÇÃO DA LOJA ("o problema foi resolvido?", com o botão
            "não resolveu" que devolvia o chamado ao técnico) e o ESCAPE do Gerente
            TI depois de 10 dias corridos sem resposta.
            Quem encerra passou a ser o próprio TÉCNICO, ao registrar o serviço —
            o bloco "Encerrar" acima. O inventário completo do que se perdeu está
            no cabeçalho do `chamados-fluxo.js`, com teste para cada ausência. */}

        {/* ==================================================================
            CANCELAMENTO — duas janelas diferentes, de propósito

            ⚠️⚠️ A LOJA só cancela em 'Aberto'; a TRIAGEM cancela em qualquer
               estado vivo. Escrever uma condição só (`!trabalhoAcabou`) para os
               dois faria o botão aparecer para o gerente num chamado já
               atribuído — e o clique voltaria 409, que é o sintoma que este
               módulo mais evita: botão que existe e não funciona.
            ================================================================== */}
        {!trabalhoAcabou(c.STATUS)
          && (podeCancelarPelaEquipe || (podeCancelarPelaLoja && c.STATUS === 'Aberto')) && (
          <>
            <button
              type="button" disabled={ocupado} style={btnAcao('#94a3b8')}
              onClick={() => setCancelamento((x) => ({ ...x, aberto: !x.aberto }))}
            >
              <XCircle size={14} /> {cancelamento.aberto ? 'Deixar como está' : 'Cancelar'}
            </button>

            {/* ==============================================================
                O PAINEL DE CANCELAMENTO — motivo obrigatório, foto OPCIONAL.

                ⚠️⚠️ SUBSTITUIU UM `window.prompt` (09/09), e não por gosto: o
                   prompt nativo não aceita arquivo, então "incluir a opção de
                   foto no cancelamento" não caberia nele. De quebra, sai um
                   diálogo que não dá para estilizar, não mostra o que já foi
                   digitado ao errar, e some inteiro se a pessoa tocar fora dele
                   no celular.

                ⚠️ O botão que ABRE virou alternador ("Deixar como está"), porque
                   um painel de ato irreversível sem saída visível faz a pessoa
                   recarregar a página para escapar.
                ============================================================== */}
            {cancelamento.aberto && (
              <div style={{
                width: '100%', padding: 16, backgroundColor: '#fffbeb',
                borderRadius: 8, border: '1px solid #fcd34d', marginTop: 10,
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 800, color: '#78350f' }}>
                  Cancelar o chamado #{c.ID}
                </p>
                {/* ⚠️ Avisar que é DEFINITIVO antes do campo, não depois do
                    clique: `Cancelado` é status final (`STATUS_FINAIS`) e não
                    existe reabertura no módulo. */}
                <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#92400e' }}>
                  Não há reabertura — depois de cancelado, um novo pedido significa abrir outro chamado.
                </p>
                <textarea
                  style={{ ...inputStyle, width: '100%', minHeight: 70, resize: 'vertical' }}
                  placeholder="Por que o chamado está sendo cancelado?"
                  value={cancelamento.motivo}
                  onChange={(e) => setCancelamento((x) => ({ ...x, motivo: e.target.value }))}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 12 }}>
                  {cancelamento.fotos.map((f) => (
                    <span key={f.url} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                      backgroundColor: '#fef3c7', borderRadius: 6, fontSize: 12, maxWidth: 200,
                    }}>
                      <ImageIcon size={13} color="#b45309" />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nome}</span>
                    </span>
                  ))}
                  {/* ⚠️ O rótulo diz "(opcional)" SEMPRE, e é o pedido literal da
                      Julia. Sem a palavra, um botão de anexo ao lado de um campo
                      obrigatório se lê como obrigatório também — e a pessoa
                      fotografa qualquer coisa para "liberar" o botão. Dois gatilhos
                      (câmera + galeria) e várias de uma vez, como nos demais. */}
                  {cancelamento.fotos.length < MAX_FOTOS && (
                    <EscolherAnexo
                      accept="image/*"
                      multiplo
                      cor="#b45309"
                      ocupado={ocupado}
                      rotulo={cancelamento.fotos.length ? 'Outra foto (opcional)' : 'Foto (opcional)'}
                      onArquivo={(a) => subirFotoCancelamento(a, c.LOJA)}
                      onArquivos={(arqs) => anexarFotosLote(arqs, {
                        momento: 'Cancelamento', loja: c.LOJA,
                        restante: MAX_FOTOS - cancelamento.fotos.length,
                        append: (f) => setCancelamento((x) => ({ ...x, fotos: [...x.fotos, f] })),
                      })}
                    />
                  )}
                </div>
                <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#a16207' }}>
                  O <strong>motivo é obrigatório</strong> — é ele que registra a decisão.
                  A foto é opcional, para quando ajudar a provar o motivo.
                  {!!cancelamento.fotos.length && <> Anexadas: <strong>{cancelamento.fotos.length}</strong> de {MAX_FOTOS}.</>}
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    type="button"
                    disabled={ocupado || !cancelamento.motivo.trim()}
                    style={{
                      ...btnAcao('#b45309'),
                      opacity: ocupado || !cancelamento.motivo.trim() ? 0.5 : 1,
                    }}
                    onClick={() => agir(
                      `/${c.ID}/cancelar`,
                      {
                        motivo: cancelamento.motivo.trim(),
                        // ⚠️ Só as URLs — o `nome` do arquivo é enfeite de tela e
                        //    não tem coluna no banco.
                        fotos: cancelamento.fotos.map((f) => f.url),
                      },
                      `Chamado #${c.ID} cancelado.`,
                    )}
                  >
                    <XCircle size={14} /> Confirmar o cancelamento
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  /**
   * O corpo do detalhe do chamado — descrição, linha do tempo, evidências e
   * ações. Vive num MODAL desde 2026-08-24; antes era uma linha expandida da
   * tabela.
   *
   * ⚠️ FUNÇÃO que devolve JSX, não componente — pela mesma razão do
   *    `acoesDoChamado`: como componente declarado aqui dentro, cada tecla
   *    digitada remontaria a subárvore e os campos perderiam o foco.
   */
  // =====================================================================
  // O MESMO CHAMADO, EM CARTÃO (celular).
  //
  // ⚠️⚠️ NÃO É A TABELA ENCOLHIDA — é outra apresentação dos mesmos dados. A
  //    tabela tem 10 colunas e 900px de largura mínima: num aparelho de 360px ela
  //    vira arrasto lateral, e o número que mais importa ("parado há") fica fora
  //    da tela justamente na hora em que se olha a fila com pressa.
  //
  // ⚠️ A HIERARQUIA MUDA junto com o formato. Na tabela tudo tem o mesmo peso,
  //    porque a coluna já diz o que é cada coisa. No cartão não há cabeçalho, então
  //    o ASSUNTO manda (é como a pessoa reconhece o chamado) e situação + dias
  //    parado sobem para a primeira linha — são a leitura de fila.
  //
  // ⚠️ É a MESMA função que devolve JSX, não um componente: vale aqui a advertência
  //    do topo do arquivo — declarar componente dentro deste faz o React remontar a
  //    cada tecla e o foco do campo se perde.
  // =====================================================================
  const cartaoDoChamado = (c) => {
    const prazo = leituraDoPrazo(c, hoje);
    const equipamento = c.EQUIPAMENTO_DESCRICAO
      ? `${c.EQUIPAMENTO_FAMILIA} — ${c.EQUIPAMENTO_DESCRICAO}`
      : null;
    return (
      <button
        key={c.ID}
        type="button"
        onClick={() => abrirDetalhe(c.ID)}
        style={{
          display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
          border: '1px solid #e2e8f0', borderLeft: `4px solid ${COR}`,
          borderRadius: 10, backgroundColor: '#fff', padding: 12, marginBottom: 10,
          // ⚠️ O cartão inteiro é o alvo de toque. Área grande é o que torna uma
          //    lista utilizável com o polegar, andando.
          font: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, fontSize: 13, color: COR, fontVariantNumeric: 'tabular-nums' }}>
            #{c.ID}
          </span>
          {selo(c.STATUS, c)}
          {/* ⚠️ O PRAZO empurrado para a direita: é o número que faz a fila ser
              lida, e no cartão ele precisa de posição fixa para o olho achar.
              (Era o "parado há"; virou o prazo do SLA em 09/09, com o parado
              como reserva de quem não tem prioridade — ver `leituraDoPrazo`.) */}
          <span style={{
            marginLeft: 'auto', fontSize: 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
            color: prazo.cor,
          }}>
            {prazo.texto}
          </span>
        </div>

        <p style={{ margin: '0 0 6px 0', fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>
          {c.TITULO}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 12, color: '#64748b' }}>
          <span style={{ fontWeight: 700, color: '#334155' }}>{nomeDoLocal(c.LOJA, c.QUIOSQUE)}</span>
          {c.TIPO_SOLICITACAO && <span>{c.TIPO_SOLICITACAO}</span>}
          {c.PRIORIDADE && <span>{c.PRIORIDADE === 'Media' ? 'Média' : c.PRIORIDADE}</span>}
        </div>

        {equipamento && (
          <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#64748b' }}>{equipamento}</p>
        )}

        <div style={{
          marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0',
          display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, color: '#94a3b8',
        }}>
          <span>{aguardandoDe(c) ? `Aguardando ${aguardandoDe(c)}` : '—'}</span>
          {/* ⚠️ TEMPORÁRIO (janela de 10 a 18/09/2026): o selo 'Auvo' explica, sem
              abrir o chamado, por que ele já entra na fila com data antiga e o
              SLA estourado. */}
          <span style={{ whiteSpace: 'nowrap' }}>
            Aberto {fmtData(c.ABERTO_EM)}{c.ORIGEM === 'Auvo' ? ' · Auvo' : ''}
          </span>
        </div>
      </button>
    );
  };

  /**
   * O aviso de erro. Existe como função porque é renderizado em DOIS lugares —
   * na página quando o modal está fechado, e dentro do modal quando ele está
   * aberto —, nunca nos dois ao mesmo tempo. Antes só existia na página, e no
   * celular (modal em tela cheia) toda falha era invisível: o técnico tocava no
   * botão e concluía que o portal não fazia nada.
   */
  const bannerErro = () => (erro ? (
    <div style={{ display: 'flex', gap: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16 }}>
      <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
      <p style={{ margin: 0, fontSize: 13, color: '#991b1b' }}>{erro}</p>
    </div>
  ) : null);

  const corpoDoDetalhe = () => {
    if (!detalhe) {
      return <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Carregando o chamado…</p>;
    }
    // ⚠️ Depois do `if (!detalhe)`: montar antes obrigaria a função a tratar o
    //    nulo, que é justamente o que a guarda acima já resolve.
    const linhaDoTempo = montarLinhaDoTempo(detalhe);
    return (
      <>
        {/* ⚠️ DEM-00009 (frente A): se este chamado NASCEU de uma recusa de
            validação, diz de onde veio e por quê, com o pai a um clique. Sem isto,
            o filho pareceria um chamado solto e ninguém ligaria à volta do problema. */}
        {detalhe.ID_CHAMADO_ORIGEM && (
          <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <p style={{ margin: 0, fontSize: 13, color: '#1e3a8a', fontWeight: 700 }}>
              Este chamado nasceu do #{detalhe.ID_CHAMADO_ORIGEM}
              {detalhe.ORIGEM_TITULO ? ` (${detalhe.ORIGEM_TITULO})` : ''}, que a loja não validou.
            </p>
            {detalhe.ORIGEM_RECUSA_MOTIVO && (
              <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                Motivo da recusa: {detalhe.ORIGEM_RECUSA_MOTIVO}
              </p>
            )}
            <button
              type="button"
              onClick={() => abrirDetalhe(detalhe.ID_CHAMADO_ORIGEM)}
              style={{ ...btnAcao(COR), marginTop: 8, padding: '6px 12px', backgroundColor: '#fff' }}
            >
              Abrir o chamado #{detalhe.ID_CHAMADO_ORIGEM}
            </button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 20 }}>
          <div>
            <p style={rotuloBloco}>Descrição da loja</p>
            <p style={{ fontSize: 13, color: '#334155', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {detalhe.DESCRICAO}
            </p>
            <p style={{ fontSize: 12, color: '#64748b', margin: '10px 0 0 0' }}>
              Tipo: <strong>{detalhe.TIPO_SOLICITACAO || '—'}</strong>
              {detalhe.PRIORIDADE && <> · Prioridade: <strong>{detalhe.PRIORIDADE === 'Media' ? 'Média' : detalhe.PRIORIDADE}</strong></>}
            </p>
            {detalhe.EQUIPAMENTO_DESCRICAO && (
              <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0 0' }}>
                Equipamento: <strong>{detalhe.EQUIPAMENTO_FAMILIA} — {detalhe.EQUIPAMENTO_DESCRICAO}</strong>
              </p>
            )}

            {/* O PEDIDO DE IMAGEM — é o que o técnico precisa ter à mão para
                buscar no DVR, por isso fica colado à descrição e não numa aba.
                ⚠️⚠️ Decide pela EXISTÊNCIA do bloco (`detalhe.imagem`), nunca
                   comparando o nome do tipo: comparação de string acentuada
                   espalhada por mais um arquivo é divergência de acento
                   esperando acontecer. */}
            {detalhe.imagem && (
              <div style={{
                marginTop: 12, padding: 12, borderRadius: 8,
                backgroundColor: COR_CLARA, border: `1px solid ${COR_A22}`,
              }}>
                <p style={{ ...rotuloBloco, marginTop: 0 }}>Imagem solicitada</p>
                <p style={{ fontSize: 13, color: '#334155', margin: 0, lineHeight: 1.7 }}>
                  {detalhe.imagem.CANAIS.includes(',') ? 'Câmeras' : 'Câmera'}{' '}
                  <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {rotuloCanais(detalhe.imagem.CANAIS)}
                  </strong><br />
                  Período: <strong>{fmtDataHora(detalhe.imagem.PERIODO_INICIO)}</strong> até{' '}
                  <strong>{fmtDataHora(detalhe.imagem.PERIODO_FIM)}</strong><br />
                  Destino:{' '}
                  <strong>
                    {DESTINOS_IMAGEM.find((d) => d.valor === detalhe.imagem.DESTINO)?.rotulo
                      || detalhe.imagem.DESTINO}
                  </strong>
                </p>
                {/* ⚠️ O MOTIVO aparece SEMPRE, não atrás de um "ver mais": é o
                    que justifica o acesso a imagem com pessoa identificável, e
                    o que ninguém abre não é lido. */}
                <p style={{ fontSize: 12, color: '#475569', margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>
                  <span style={{ color: '#64748b' }}>Motivo:</span> {detalhe.imagem.MOTIVO}
                </p>
              </div>
            )}

            {/* O PEDIDO DE CHIP (DEM-00027) — colaborador, motivo, linha e obs.
                ⚠️ Decide pela EXISTÊNCIA do bloco (`detalhe.chip`), como o imagem. */}
            {detalhe.chip && (
              <div style={{
                marginTop: 12, padding: 12, borderRadius: 8,
                backgroundColor: COR_CLARA, border: `1px solid ${COR_A22}`,
              }}>
                <p style={{ ...rotuloBloco, marginTop: 0 }}>Solicitação de chip</p>
                <p style={{ fontSize: 13, color: '#334155', margin: 0, lineHeight: 1.7 }}>
                  Colaborador: <strong>{detalhe.chip.COLABORADOR}</strong><br />
                  Motivo: <strong>{ROTULO_MOTIVO_CHIP[detalhe.chip.MOTIVO] || detalhe.chip.MOTIVO}</strong>
                  {detalhe.chip.LINHA_ATUAL && (
                    <>
                      <br />Linha atual: <strong>{detalhe.chip.LINHA_ATUAL}</strong>
                    </>
                  )}
                </p>
                {detalhe.chip.OBSERVACAO && (
                  <p style={{ fontSize: 12, color: '#475569', margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>
                    <span style={{ color: '#64748b' }}>Observação:</span> {detalhe.chip.OBSERVACAO}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <p style={rotuloBloco}>Linha do tempo</p>
            {/* ⚠️⚠️ A LISTA É MONTADA E ORDENADA POR DATA (`montarLinhaDoTempo`),
                não escrita ato a ato no JSX. Era escrita aqui até 28/08, com um
                lugar fixo para cada ato — o que funcionava porque cada ato só
                podia acontecer na sua vez. A CORREÇÃO do chamado quebrou isso:
                ela é permitida em qualquer estado vivo, e o caso mais comum é o
                mais cedo de todos (o TI acerta o tipo assim que a loja abre).
                O porquê inteiro está no cabeçalho da função. */}
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
              {linhaDoTempo.atos.map((ato, i) => (
                <li key={`${ato.t}-${i}`}>{ato.texto}</li>
              ))}
            </ul>
            {/* A VISITA MARCADA fica FORA da lista ordenada: é data FUTURA, um
                compromisso e não um ato que aconteceu. Ordenada junto, iria
                parar depois do encerramento. */}
            {linhaDoTempo.visita && (
              <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#334155' }}>
                Visita marcada para <strong>{linhaDoTempo.visita}</strong>
              </p>
            )}
          </div>
        </div>

        {/* ==================================================================
            O QUE O CHAMADO ESTÁ ESPERANDO (Julia, 31/08/2026).

            ⚠️ Aparece SEMPRE que houver o dado, não só enquanto o status é
               `Finalizado`: depois de retomado e encerrado, o número do Invgate e
               a lista de peças continuam sendo o registro do que foi feito e
               comprado. Escondê-los ao retomar faria a evidência sumir justamente
               quando alguém for conferir a nota.
            ================================================================== */}
        {detalhe.INVGATE_CHAMADO && (
          <div style={{ marginBottom: 20 }}>
            <p style={rotuloBloco}>Escalado para o Invgate</p>
            <p style={{ fontSize: 13, color: '#334155', margin: 0 }}>
              Chamado <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{detalhe.INVGATE_CHAMADO}</strong> na Sonda.
              {detalhe.STATUS === 'Finalizado' && (
                <span style={{ color: '#92400e' }}> O atendimento está parado esperando a resposta deles.</span>
              )}
            </p>
          </div>
        )}

        {/* ⚠️⚠️ O PAINEL DE PEÇAS VIROU COMPONENTE em 2026-09-01, junto com a 3ª
            saída: passaram a ser DUAS listas (compra e reparo) e a de compra
            ganhou EDIÇÃO — o Gerente de TI completa fornecedor e valor depois,
            porque é ele quem cota. Inflar esta tela (já com 2.400 linhas) com
            um formulário editável dentro de outro era o caminho para ninguém
            mais achar nada aqui. A regra de negócio está lá, comentada.
            ⚠️ `aoSalvar` recarrega o DETALHE, não a lista: gravar cotação não
            muda status nem posição na fila — fechar o modal a cada preço
            digitado faria o Gerente de TI reabrir o chamado a cada linha. */}
        <ChamadosPecas
          pecas={detalhe.pecas}
          idChamado={detalhe.ID}
          podeEditar={podeExecutar}
          status={detalhe.STATUS}
          aoSalvar={async () => {
            const r = await fetch(`${API}/${detalhe.ID}`, { headers: auth() });
            if (r.ok) setDetalhe(await r.json());
          }}
        />

        {detalhe.OBSERVACAO_TECNICO && (
          <div style={{ marginBottom: 20 }}>
            <p style={rotuloBloco}>Registro do técnico</p>
            <p style={{ fontSize: 13, color: '#334155', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {detalhe.OBSERVACAO_TECNICO}
            </p>
          </div>
        )}

        {/* ⚠️⚠️ MINIATURAS DE VERDADE, não links (pedido da Julia, 2026-08-21, no
            padrão do formulário de Certificação de Equipamentos). E não era só
            estética: a `webUrl` do SharePoint é AUTENTICADA, então o link anterior
            **não abria** — clicar dependia de o usuário ter sessão do M365 no
            navegador. O `ImagemSharePoint` busca os bytes pelo PROXY do backend
            (que tem o token do Graph) e exibe. */}
        {!!detalhe.fotos?.length && (
          <div>
            <p style={rotuloBloco}>Evidências</p>
            {/* Separadas por MOMENTO: a foto do problema, a do serviço feito e a
                do cancelamento respondem perguntas diferentes, e misturá-las numa
                fileira só apaga a distinção.

                ⚠️⚠️ MAPA, NUNCA TERNÁRIO — e aqui isso é o conserto de uma
                armadilha real, não preferência de estilo. Com dois momentos, um
                `ehConclusao ? A : B` funcionava. Ao entrar o terceiro
                ('Cancelamento', 09/09), o mesmo ternário passaria a rotular a
                foto do cancelamento como "O problema, pela loja", na cor do
                módulo, **sem erro nenhum**. É a quinta vez que esta armadilha
                aparece no módulo (`rotuloDoEvento`, `ESPERA_POR_MOTIVO`,
                `VAZIO_DA_ABA`, `PRAZOS`) — momento novo nunca deve herdar o
                texto de outro.

                ⚠️ A ORDEM DE EXIBIÇÃO é a das chaves deste objeto, que é a
                cronologia do ato. Não é o `ORDER BY MOMENTO` do servidor (que usa
                a ordem de declaração do ENUM) — as duas coincidem de propósito. */}
            {Object.entries(MOMENTO_FOTO).map(([momento, m]) => {
              const doMomento = detalhe.fotos.filter((f) => f.MOMENTO === momento);
              if (!doMomento.length) return null;
              return (
                <div key={momento} style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, margin: '0 0 6px 0', color: m.cor }}>
                    {`${m.titulo} (${doMomento.length})`}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {doMomento.map((f) => (
                      <ImagemSharePoint
                        key={f.ID}
                        apiBase={API}
                        url={f.FOTO_URL}
                        alt={m.alt}
                        size={110}
                        borderColor={m.borda}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {/* ⚠️ Foto com MOMENTO que este mapa não conhece é ANUNCIADA, nunca
                escondida: é o sintoma de valor novo no ENUM sem lugar aqui, e
                evidência invisível é pior que um aviso feio. Mesmo desenho do
                rodapé "peça em um status que esta tela ainda não agrupa" da fila
                de reparo. */}
            {(() => {
              const orfas = detalhe.fotos.filter((f) => !MOMENTO_FOTO[f.MOMENTO]);
              if (!orfas.length) return null;
              return (
                <p style={{ fontSize: 11, color: '#b45309', margin: '0 0 6px 0' }}>
                  {orfas.length === 1
                    ? '1 evidência em um momento que esta tela ainda não exibe.'
                    : `${orfas.length} evidências em um momento que esta tela ainda não exibe.`}
                </p>
              );
            })()}
          </div>
        )}

        {acoesDoChamado(detalhe)}

        {/* A CIÊNCIA DO GERENTE, NO CHAMADO JÁ ENCERRADO.
            ⚠️ Só depois de ENCERRADO: validar antes seria atestar um serviço que
            não aconteceu.
            ⚠️ VALIDAR NÃO MUDA O STATUS — a validação da loja como etapa continua
            removida desde 21/08. Isto registra CIÊNCIA, e é por isso que o bloco
            fica junto do PDF (o documento que circula) e não junto das ações da
            esteira.

            ⚠️⚠️ NÃO É CÓPIA DO BLOCO DO ENCERRAMENTO — é a EXIBIÇÃO do que foi
            registrado lá, mais a porta de entrada do que ficou para trás:
              • chamado novo já chega aqui VALIDADO (é obrigatório para encerrar,
                desde 27/08) e este bloco só mostra quem e quando;
              • chamado encerrado ANTES de 27/08 chega SEM validação — e é só para
                ele que o seletor abaixo aparece.
            Tirar este bloco por parecer repetido deixaria esse passado sem porta
            nenhuma. Os dois usam a mesma regra no servidor (`conferirValidacao`);
            o que muda é a rota — aqui `PUT /:id/validar`, lá os campos junto do
            `PUT /:id/encerrar`.

            ⚠️ E há um TERCEIRO caso, só de leitura: os chamados da janela de 24 a
            27/08, que têm a ASSINATURA DESENHADA. Nada mais escreve nela; ela
            continua sendo exibida para quem a tem. */}
        {['Encerrado', 'Validado'].includes(detalhe.STATUS) && (() => {
          const validado = !!detalhe.VALIDADO_EM;
          const temAssinaturaLegado = !!detalhe.ASSINATURA_URL;
          const registrado = validado || temAssinaturaLegado;
          return (
            <div style={{
              marginTop: 18, padding: 16, borderRadius: 8,
              backgroundColor: registrado ? '#f0fdf4' : '#ecfeff',
              border: `1px solid ${registrado ? '#bbf7d0' : '#a5f3fc'}`,
            }}>
              <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                Validação da loja
              </p>

              {validado ? (
                /* O modelo atual: quem validou, o papel e o carimbo de data/hora. */
                <p style={{ margin: 0, fontSize: 13, color: '#166534', lineHeight: 1.6 }}>
                  <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} />{' '}
                  Validado por <strong>{detalhe.VALIDADO_POR_NOME}</strong>
                  {detalhe.VALIDADO_POR_PERFIL && (
                    <> ({rotuloValidador(detalhe.VALIDADO_POR_PERFIL)})</>
                  )}
                  <br />
                  <span style={{ color: '#15803d' }}>em {fmtDataHora(detalhe.VALIDADO_EM)}</span>
                </p>
              ) : temAssinaturaLegado ? (
                /* LEGADO (24–27/08): a assinatura desenhada. Somente leitura. */
                <>
                  <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#166534' }}>
                    Assinado por <strong>{detalhe.ASSINATURA_NOME}</strong> em {fmtDataHora(detalhe.ASSINATURA_EM)}.
                  </p>
                  <ImagemSharePoint apiBase={API} url={detalhe.ASSINATURA_URL}
                    alt="Assinatura do gerente" size={130} borderColor="#86efac" />
                </>
              ) : (
                /* ==========================================================
                   O ATO 4, ESPERANDO ACONTECER (Julia, 31/08/2026).

                   ⚠️⚠️ AQUI HAVIA UM SELETOR DE GERENTE, e ele SUMIU — é a
                      mudança inteira num lugar só. O técnico escolhia o nome de
                      quem recebeu o serviço; agora quem valida é QUEM ESTÁ
                      LOGADO, clicando. Um seletor aqui seria o convite de volta
                      para validar em nome de outra pessoa.

                   ⚠️ O bloco aparece para TODO MUNDO que abre o chamado — é ele
                      que diz "falta a loja dar ciência". O BOTÃO é que só aparece
                      para quem pode validar; quem não pode lê a quem pedir.
                   ========================================================== */
                <>
                  <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#155e75', lineHeight: 1.5 }}>
                    O serviço foi encerrado pelo técnico e <strong>aguarda a ciência da loja</strong>.
                    Quem valida é o gerente, o gerente de plantão ou o coordenador de
                    qualidade de <strong>{detalhe.LOJA}</strong> — a data e a hora são
                    carimbadas pelo portal no momento do clique.
                  </p>
                  {podeValidar ? (
                    <>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          disabled={ocupado}
                          onClick={() => agir(`/${detalhe.ID}/validar`, {},
                            `Chamado #${detalhe.ID} validado. Obrigado!`)}
                          style={{ ...btnAcao('#16a34a'), padding: '9px 16px', backgroundColor: '#16a34a', color: '#fff' }}
                        >
                          {/* ⚠️ O rótulo diz o que o clique AFIRMA. "Validar" seco não
                              diz que se está atestando ter recebido o serviço. */}
                          <CheckCircle2 size={14} /> Validar chamado
                        </button>
                        {/* ⚠️ DEM-00009 (frente A): o outro lado da validação. Não
                            devolve o chamado ao técnico — abre um FILHO ligado a este. */}
                        <button
                          type="button"
                          disabled={ocupado}
                          onClick={() => setRecusa((r) => ({ ...r, aberto: !r.aberto }))}
                          style={{ ...btnAcao('#b45309'), padding: '9px 16px', backgroundColor: '#fff', color: '#b45309' }}
                        >
                          <AlertCircle size={14} /> Não validar chamado
                        </button>
                      </div>
                      {recusa.aberto && (
                        <div style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                          <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                            Diga o que continua errado e anexe uma foto do problema. Isto
                            <strong> não reabre</strong> este chamado — abre um novo, ligado a
                            ele, que volta para a triagem do TI.
                          </p>
                          <textarea
                            style={{ ...inputStyle, width: '100%', minHeight: 70, resize: 'vertical' }}
                            placeholder="O que não resolveu?"
                            value={recusa.motivo}
                            onChange={(e) => setRecusa((r) => ({ ...r, motivo: e.target.value }))}
                          />
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 10 }}>
                            {recusa.fotos.map((f) => (
                              <span key={f.url} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                                backgroundColor: '#fef3c7', borderRadius: 6, fontSize: 12, maxWidth: 200,
                              }}>
                                <ImageIcon size={13} color="#b45309" />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nome}</span>
                              </span>
                            ))}
                            {recusa.fotos.length < MAX_FOTOS && (
                              <EscolherAnexo
                                accept="image/*"
                                multiplo
                                cor="#b45309"
                                ocupado={ocupado}
                                rotulo={recusa.fotos.length ? 'Outra foto' : 'Foto do problema'}
                                onArquivo={(a) => subirFotoRecusa(a, detalhe.LOJA)}
                                onArquivos={(arqs) => anexarFotosLote(arqs, {
                                  momento: 'Abertura', loja: detalhe.LOJA,
                                  restante: MAX_FOTOS - recusa.fotos.length,
                                  append: (f) => setRecusa((r) => ({ ...r, fotos: [...r.fotos, f] })),
                                })}
                              />
                            )}
                          </div>
                          <button
                            type="button"
                            disabled={ocupado || !recusa.motivo.trim() || !recusa.fotos.length}
                            onClick={() => agir(`/${detalhe.ID}/recusar-validacao`, {
                              motivo: recusa.motivo.trim(),
                              fotos: recusa.fotos.map((f) => f.url),
                            }, 'Registrado. Abrimos um chamado novo ligado a este, e ele volta para a triagem.')}
                            style={{
                              ...btnAcao('#b45309'), marginTop: 10, padding: '9px 16px',
                              backgroundColor: (recusa.motivo.trim() && recusa.fotos.length) ? '#b45309' : '#e2e8f0',
                              color: (recusa.motivo.trim() && recusa.fotos.length) ? '#fff' : '#94a3b8',
                              borderColor: (recusa.motivo.trim() && recusa.fotos.length) ? '#b45309' : '#e2e8f0',
                            }}
                          >
                            <AlertCircle size={14} /> Abrir chamado novo (não resolveu)
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                      {/* ⚠️ Explica a ausência do botão. Sem esta linha, o técnico
                          que abre o chamado encerrado vê um bloco sem ação e
                          conclui que a tela quebrou. */}
                      Seu perfil não valida serviço — peça ao gerente da loja para
                      confirmar aqui, no portal.
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* PDF do chamado (pedido da Julia, 2026-08-24).
            ⚠️ Aparece SEMPRE, não só no encerrado: o documento também serve para
            o técnico levar o relato à loja antes de atender. O próprio PDF diz,
            em palavras, quando o serviço ainda não terminou — em vez de deixar o
            campo em branco, que num documento se lê como esquecimento.
            ⚠️ O que acontece ao tocar depende do aparelho — no celular o PDF
            BAIXA, no computador ABRE em aba. O porquê está no `abrirPdf`. */}
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
          <button type="button" onClick={() => abrirPdf(detalhe.ID)} disabled={ocupado}
            style={{ ...btnAcao('#475569'), padding: '9px 16px' }}>
            <FileText size={14} /> Gerar PDF do chamado
          </button>
          <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 10 }}>
            Registro completo: o pedido da loja, o serviço do técnico, a linha do
            tempo e as fotos.
          </span>
        </div>
      </>
    );
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>Chamados</h2>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            {/* ⚠️ Espelha o `PERFIS_REDE_TODA` do `server-chamados.js` — quem
                está aqui e não lá (ou o contrário) faz a tela ANUNCIAR um
                alcance que o recorte do servidor não entrega. */}
            {['Admin', 'Gerente TI', 'TI', 'Diretoria'].includes(perfil)
              ? 'Rede inteira'
              : `Loja ${user?.loja || '—'}`} ·{' '}
            {/* ⚠️⚠️ ESTE NÚMERO E O DO BOTÃO DA ABA RESPONDEM PERGUNTAS
                DIFERENTES, e é aqui que isso fica legível. O do botão é "quanto
                trabalho existe naquela aba" (vem do servidor e IGNORA os filtros
                — as outras abas não estão em memória para serem contadas
                filtradas); este é "quanto está à vista agora". Com filtro ligado
                eles DIVERGEM, e sem o "de N" a pessoa leria isso como bug. */}
            {filtroAtivo
              ? `${filtrados.length} de ${chamados.length} chamado(s)`
              : `${chamados.length} chamado(s)`}
          </p>
        </div>
        <button type="button" onClick={carregar} style={btnAcao(COR)}>
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* ---------------------------------------------------------------
          ABAS — desde 01/09/2026 elas SÃO a esteira, um ato por aba:
            Em aberto → Atribuído → Em atendimento → Encerrado
          mais *Cancelados* (que não é passo) e *Todos*.

          ⚠️ A lista vem do `chamados-abas.js` (espelho do backend), NUNCA
             escrita à mão aqui: era a divisão em duas gavetas literais neste
             JSX que permitia a tela e a query discordarem.

          ⚠️⚠️ O CONTADOR NÃO É ENFEITE. Com as abas espelhando a esteira, o
             `Encerrado` saiu de *Em aberto* — e ele estava lá desde 31/08
             justamente para a validação da loja existir como TRABALHO ("a
             cobrança é a fila, não a trava"). O número no botão é o que
             substitui essa garantia: sem ele, o chamado que espera o gerente
             fica numa aba que ninguém abre.
          --------------------------------------------------------------- */}
      {/* ---------------------------------------------------------------
          A FILA (17/09/2026) — T.I ou Manutenção.

          ⚠️ Fica ACIMA das abas, e separado delas, porque não é o mesmo tipo de
             escolha: as abas são passos da esteira DENTRO de uma fila; isto troca
             a fila inteira. Lado a lado, a pessoa leria seis gavetas irmãs.
          --------------------------------------------------------------- */}
      {/* ⚠️ O SELETOR DE FILA SAIU DAQUI em 17/09/2026, no mesmo dia em que
          entrou: quem troca a fila é a FAIXA do hub, que fica acima das abas e
          vale para o módulo inteiro. Dois controles para a mesma pergunta viram
          duas verdades — e a que estivesse errada não daria erro nenhum. */}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* ⚠️ A aba de chip (DEM-00027) é só da fila de T.I. — na fila de
            Manutenção ela não aparece (chip não existe lá). */}
        {(equipe === 'Manutencao' ? ABAS.filter((a) => a.id !== 'chip') : ABAS).map(({ id, rotulo }) => {
          const ativa = aba === id;
          // ⚠️ `null` = não sei (contagem não chegou): a aba fica SEM número.
          //    Ausência na resposta é zero de verdade — status sem nenhum chamado
          //    não vem no `GROUP BY`.
          const n = contagem ? (contagem[id] ?? 0) : null;
          return (
            <button
              key={id} type="button" onClick={() => trocarAba(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px 8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                border: ativa ? `2px solid ${COR}` : '2px solid transparent',
                backgroundColor: ativa ? COR_CLARA : '#f8fafc',
                color: ativa ? COR : '#64748b',
              }}
            >
              {rotulo}
              {n !== null && (
                <span style={{
                  minWidth: 22, padding: '1px 6px', borderRadius: 999,
                  fontSize: 12, fontWeight: 800, textAlign: 'center',
                  // ⚠️ `tabular-nums`: sem isso o botão MUDA DE LARGURA quando a
                  //    contagem passa de 9 para 10, e a fila de abas dança a cada
                  //    ação — os dígitos precisam ter todos a mesma medida.
                  fontVariantNumeric: 'tabular-nums',
                  backgroundColor: ativa ? COR : '#e2e8f0',
                  color: ativa ? '#ffffff' : '#64748b',
                  // ⚠️ Aba vazia fica ESMAECIDA em vez de esconder o zero: o "0"
                  //    é informação ("conferi, não tem"), e sumir com ele deixaria
                  //    a aba igual à que ainda não respondeu.
                  opacity: n === 0 ? 0.45 : 1,
                }}>
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* FILTROS — loja · tipo · prioridade · situação · período (aaaa-mm).
          ⚠️ As opções saem do que EXISTE na lista carregada: filtro que oferece
          opção sem nenhum resultado faz a pessoa duvidar da tela. */}
      <div style={{
        display: ehMobile ? 'block' : 'flex',
        flexWrap: 'wrap', gap: 8, alignItems: 'center',
        marginBottom: 16, padding: 12, backgroundColor: '#fafafa',
        borderRadius: 8, border: `1px solid ${COR_CLARA}`,
      }}>
        {ehMobile ? (
          /* No celular o rótulo vira o BOTÃO que abre/fecha, e carrega o placar —
             assim, mesmo recolhido, o bloco diz que está escondendo algo. */
          <button
            type="button"
            onClick={() => setFiltrosAbertos((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              font: 'inherit', minHeight: 32,
            }}
          >
            <Filter size={14} color={filtroAtivo ? '#b45309' : '#94a3b8'} />
            <span style={{
              fontSize: 12, fontWeight: 800, letterSpacing: '0.03em',
              textTransform: 'uppercase', color: filtroAtivo ? '#b45309' : '#94a3b8',
            }}>
              Filtros
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: filtroAtivo ? '#b45309' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
              {filtroAtivo
                ? `${filtrados.length} de ${chamados.length}`
                : `${chamados.length} chamado(s)`}
            </span>
            {filtrosAbertos ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
          </button>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            Filtros
          </span>
        )}

        {/* Os seletores em si. No celular, um por linha e só com o bloco aberto. */}
        <div style={{
          display: (ehMobile && !filtrosAbertos) ? 'none' : (ehMobile ? 'grid' : 'contents'),
          gap: 8, marginTop: ehMobile ? 10 : 0,
        }}>

        <select style={inputStyle} value={filtros.loja} onChange={(e) => mudarFiltro('loja', e.target.value)}>
          <option value="">Todas as lojas</option>
          {opcoes('LOJA').map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select style={inputStyle} value={filtros.tipo} onChange={(e) => mudarFiltro('tipo', e.target.value)}>
          <option value="">Todos os tipos</option>
          {tiposDoFiltro.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select style={inputStyle} value={filtros.prioridade} onChange={(e) => mudarFiltro('prioridade', e.target.value)}>
          <option value="">Toda prioridade</option>
          {PRIORIDADES.map((v) => <option key={v} value={v}>{v === 'Media' ? 'Média' : v}</option>)}
          {/* Chamado ainda não triado não tem prioridade — e essa é uma fila. */}
          <option value="sem">Sem prioridade (a triar)</option>
        </select>

        {/* ⚠️ ESCONDIDO NAS ABAS DE UM ÚNICO STATUS (Em aberto · Atribuído ·
            Cancelados): um `<select>` com uma opção só é ruído que sugere
            escolha onde não há. Quem limpa o valor preso é o `trocarAba` — ver o
            comentário lá, porque filtro escondido E ativo é o pior dos dois
            mundos. `?? 99` = a aba *Todos* (sem lista de status) mostra o
            seletor. */}
        {(statusDaAba(aba)?.length ?? 99) > 1 && (
          <select style={inputStyle} value={filtros.status} onChange={(e) => mudarFiltro('status', e.target.value)}>
            <option value="">Toda situação</option>
            {opcoes('STATUS').map((v) => <option key={v} value={v}>{ROTULO_STATUS[v] || v}</option>)}
          </select>
        )}

        <select style={inputStyle} value={filtros.periodo} onChange={(e) => mudarFiltro('periodo', e.target.value)}>
          <option value="">Todo o período</option>
          {mesesDisponiveis.map((m) => {
            const [a, mm] = m.split('-');
            return <option key={m} value={m}>{`${mm}/${a}`}</option>;
          })}
        </select>

        {filtroAtivo && (
          <button type="button" onClick={() => { setFiltros(FILTROS_VAZIOS); setPagina(1); }}
            style={{ ...btnAcao('#64748b'), padding: '10px 12px', minHeight: ehMobile ? 44 : undefined, justifyContent: 'center' }}>
            <XCircle size={13} /> Limpar
          </button>
        )}

        {/* ⚠️ Quando há filtro, a tela diz QUANTO ela está escondendo. Sem isso,
            "3 chamados" com um filtro esquecido se lê como "só há 3".
            No celular esse placar já está no botão que abre o bloco — repeti-lo
            aqui o esconderia justamente quando o bloco está fechado. */}
        {!ehMobile && (
          <span style={{ fontSize: 12, color: filtroAtivo ? '#b45309' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
            {filtroAtivo
              ? `${filtrados.length} de ${chamados.length} (filtrado)`
              : `${chamados.length} chamado(s)`}
          </span>
        )}
        </div>
      </div>

      {/* ⚠️ Só fora do modal: com o modal aberto o banner é renderizado DENTRO
          dele (ver `bannerErro`). Este fica atrás da janela — no celular, que é
          tela cheia, a mensagem ficaria invisível. */}
      {!expandido && bannerErro()}
      {aviso && (
        <div style={{ display: 'flex', gap: 8, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>{aviso}</p>
        </div>
      )}

      {carregando ? (
        <p style={{ fontSize: 14, color: '#64748b' }}>Carregando…</p>
      ) : !chamados.length ? (
        <div style={{ padding: 40, textAlign: 'center', backgroundColor: '#fafafa', borderRadius: 12 }}>
          <CheckCircle2 size={32} color="#94a3b8" />
          <p style={{ fontSize: 14, color: '#64748b', margin: '12px 0 0 0' }}>
            {/* ⚠️ Mapa com fallback — ver `VAZIO_DA_ABA` no topo do arquivo e o
                porquê de não ser ternário (é a 3ª vez que esse bug aparece
                neste módulo). */}
            {VAZIO_DA_ABA[aba] || 'Nenhum chamado nesta lista.'}
          </p>
        </div>
      ) : (
        <div style={{ overflowX: ehMobile ? 'visible' : 'auto' }}>
          {/* ⚠️ NO CELULAR NÃO HÁ CABEÇALHO PARA CLICAR, então a ordenação vira um
              seletor. Sem ele, trocar de layout SILENCIOSAMENTE removeria um
              recurso da tela — a pessoa no celular ficaria presa a uma ordem só. */}
          {ehMobile && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
              fontSize: 12, color: '#64748b',
            }}>
              <ArrowUpDown size={14} style={{ flexShrink: 0 }} />
              <select
                value={ordem.campo}
                onChange={(e) => ordenarPor(e.target.value, true)}
                style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                aria-label="Ordenar por"
              >
                {COLUNAS.map((col) => (
                  <option key={col.campo} value={col.campo}>{col.rotulo}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => ordenarPor(ordem.campo)}
                title={ordem.asc ? 'Crescente' : 'Decrescente'}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  border: '1px solid #cbd5e1', backgroundColor: '#fff',
                  color: COR, cursor: 'pointer',
                }}
              >
                {ordem.asc ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
              </button>
            </div>
          )}

          {ehMobile ? (
            <div>{daPagina.map(cartaoDoChamado)}</div>
          ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)` }}>
                {COLUNAS.map((col) => (
                  <th
                    key={col.campo}
                    onClick={() => ordenarPor(col.campo)}
                    title={`Ordenar por ${col.rotulo}`}
                    style={{
                      ...thStyle,
                      textAlign: col.direita ? 'right' : 'left',
                      cursor: 'pointer', userSelect: 'none',
                    }}
                  >
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      flexDirection: col.direita ? 'row-reverse' : 'row',
                    }}>
                      {col.rotulo}
                      {/* ⚠️ A seta da coluna ATIVA é sólida; as demais ficam
                          esmaecidas mas PRESENTES — é o que anuncia que a coluna
                          é clicável. Só mostrar na ativa esconde o recurso. */}
                      {ordem.campo === col.campo
                        ? (ordem.asc ? <ArrowUp size={13} /> : <ArrowDown size={13} />)
                        : <ArrowUpDown size={12} style={{ opacity: 0.4 }} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daPagina.map((c) => {
                const prazo = leituraDoPrazo(c, hoje);
                const aberto = expandido === c.ID;
                return (
                    <tr
                      key={c.ID}
                      onClick={() => abrirDetalhe(c.ID)}
                      style={{ cursor: 'pointer', backgroundColor: aberto ? COR_CLARA : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{c.ID}</td>
                      {/* ⚠️ Loja + quiosque na MESMA célula, sem coluna nova: só
                          três lojas têm quiosque, e uma coluna vazia em dez delas
                          rouba largura de uma tabela que já tem 10 colunas. */}
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{nomeDoLocal(c.LOJA, c.QUIOSQUE)}</td>
                      <td style={{ ...tdStyle, maxWidth: 280 }}>{c.TITULO}</td>
                      <td style={{ ...tdStyle, color: '#64748b', whiteSpace: 'nowrap' }}>{c.TIPO_SOLICITACAO || '—'}</td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>
                        {c.EQUIPAMENTO_DESCRICAO ? `${c.EQUIPAMENTO_FAMILIA} — ${c.EQUIPAMENTO_DESCRICAO}` : '—'}
                      </td>
                      <td style={tdStyle}>{c.PRIORIDADE === 'Media' ? 'Média' : (c.PRIORIDADE || '—')}</td>
                      <td style={tdStyle}>{selo(c.STATUS, c)}</td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>{aguardandoDe(c) || '—'}</td>
                      {/* ⚠️ tabular-nums: números em coluna têm de alinhar.
                          ⚠️ `title` leva a explicação (de qual perna é o prazo, se
                          está pausado): a célula é estreita e "Fora há 6h" sozinho
                          não diz se é o atendimento ou a solução. */}
                      <td
                        title={prazo.titulo}
                        style={{
                          ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                          fontWeight: 700, whiteSpace: 'nowrap', color: prazo.cor,
                        }}
                      >
                        {prazo.texto}
                      </td>
                      <td style={{ ...tdStyle, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtData(c.ABERTO_EM)}</td>
                    </tr>
                );
              })}
            </tbody>
          </table>
          )}
          {/* Paginação compartilhada com o Compras, recolorida com a cor deste
              módulo (ver ARQUITETURA.md › Paleta dos módulos). */}
          <Paginacao
            total={filtrados.length}
            pagina={paginaValida}
            onMudar={(p) => { setPagina(p); setExpandido(null); }}
            cor={COR}
            fundo={COR_CLARA}
          />
        </div>
      )}

      {/* =====================================================================
          MODAL DO CHAMADO (pedido da Julia, 2026-08-24).
          Antes o detalhe abria como LINHA EXPANDIDA da tabela.

          ⚠️ POR QUE MODAL E NÃO PÁGINA SEPARADA: a página daria URL
          compartilhável, mas PERDERIA o estado da lista — filtro, ordenação e
          página, que acabaram de ser construídos. Abrir e fechar um chamado
          zeraria o recorte a cada vez, e refazer o filtro seria o trabalho.
          O preço aceito é não poder mandar "olha o /chamados/14" para alguém.

          ⚠️ Fecha por três caminhos, de propósito: o X, a tecla Esc e o clique no
          fundo. Modal com uma única saída é a que prende quem não achou o botão.
          ===================================================================== */}
      {expandido !== null && (
        <div
          role="presentation"
          onClick={fecharDetalhe}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.55)',
            display: 'flex', justifyContent: 'center',
            // ⚠️ NO CELULAR O MODAL É TELA CHEIA. Os 40px de respiro que enquadram
            //    a janela no computador consomem, num aparelho de 360×640, o espaço
            //    de duas linhas de formulário — e este modal tem formulário, foto e
            //    campo de assinatura dentro.
            alignItems: ehMobile ? 'stretch' : 'flex-start',
            padding: ehMobile ? 0 : '40px 16px', zIndex: 1000, overflowY: 'auto',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Chamado ${expandido}`}
            /* ⚠️ Sem isto, clicar em qualquer campo DENTRO do modal borbulharia
               para o fundo e fecharia a janela no meio do preenchimento. */
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', width: '100%', maxWidth: ehMobile ? '100%' : 980,
              // Sem cantos arredondados em tela cheia: canto redondo encostado na
              // borda do aparelho mostra o fundo e parece defeito de renderização.
              borderRadius: ehMobile ? 0 : 14,
              boxShadow: ehMobile ? 'none' : '0 20px 50px rgba(0,0,0,0.30)',
              overflow: 'hidden',
              // ⚠️ Em tela cheia a caixa vira uma coluna que ocupa a altura toda:
              //    é o que mantém o cabeçalho colado no topo e faz SÓ o corpo rolar.
              display: ehMobile ? 'flex' : 'block',
              flexDirection: 'column', minHeight: ehMobile ? '100dvh' : undefined,
            }}
          >
            {/* Cabeçalho fixo: identifica o chamado mesmo com o corpo rolado. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, padding: ehMobile ? '14px 16px' : '18px 24px', flexShrink: 0,
              background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`, color: '#fff',
            }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 800, opacity: 0.85, letterSpacing: '0.04em' }}>
                  CHAMADO #{expandido}{detalhe?.LOJA ? ` · ${nomeDoLocal(detalhe.LOJA, detalhe.QUIOSQUE)}` : ''}
                </p>
                {/* ⚠️ No celular o assunto NÃO é cortado com reticências: ali ele
                    é a única forma de reconhecer o chamado, e quase todo assunto
                    passa de uma linha numa tela estreita. Deixa quebrar em duas. */}
                <h3 style={{
                  margin: '2px 0 0 0', fontSize: ehMobile ? 15 : 19, fontWeight: 800,
                  lineHeight: 1.3,
                  ...(ehMobile
                    ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                    : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                }}>
                  {detalhe?.TITULO || 'Carregando…'}
                </h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {detalhe && selo(detalhe.STATUS, detalhe)}
                <button
                  type="button" onClick={fecharDetalhe} aria-label="Fechar"
                  style={{
                    background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 8,
                    color: '#fff', cursor: 'pointer', display: 'flex',
                    // ⚠️ 40px no celular: no aparelho, o X é a saída mais usada do
                    //    modal (Esc não existe e o fundo fica coberto).
                    alignItems: 'center', justifyContent: 'center',
                    padding: ehMobile ? 0 : 8,
                    width: ehMobile ? 40 : undefined, height: ehMobile ? 40 : undefined,
                    flexShrink: 0,
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Corpo rolável: o detalhe tem foto e formulários, e não cabe em
                tela de notebook sem rolagem. `maxHeight` deixa o cabeçalho e o
                fundo visíveis, para o caminho de saída nunca sair de vista. */}
            <div style={{
              padding: ehMobile ? 16 : 24,
              // Em tela cheia quem limita a altura é o `flex:1` da coluna, não uma
              // conta com `100vh` — que no celular erra por causa da barra do
              // navegador que aparece e some ao rolar.
              ...(ehMobile
                ? { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }
                : { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }),
            }}>
              {bannerErro()}
              {corpoDoDetalhe()}
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ A legenda acompanhou a troca da coluna (09/09). Sem isso ela
          explicaria um "Parado há" que já não existe — e é justamente a linha em
          que a pessoa vai procurar o significado da coluna nova. */}
      <p style={{ fontSize: 12, color: '#94a3b8', margin: '18px 0 0 0', lineHeight: 1.6 }}>
        <Clock size={12} style={{ verticalAlign: -2 }} /> <strong>Prazo</strong> é o SLA do
        chamado, contado da <em>abertura</em>: enquanto ninguém assume, é o prazo de
        <strong> atendimento</strong>; depois, o de <strong>solução</strong>. Ele{' '}
        <strong>pausa</strong> quando o chamado espera a loja ou um terceiro — e aí a
        coluna mostra a <em>previsão</em> que o técnico registrou, não uma contagem.
        <br />
        Chamado <strong>sem prioridade</strong> não tem prazo (é a triagem que define), e
        nesses a coluna cai para os <strong>dias parados</strong> desde o último{' '}
        <em>ato</em> — que não conta salvamento de sistema, só o que uma pessoa fez.
      </p>
    </div>
  );
};

export default ChamadosConsulta;
